import {
  createSelector,
  createStructuredSelector,
  type Selector,
} from 'reselect';

import {type Ref as CustomRunColorsRef} from '../../../../state/views/customRunColors/types';
import {type Ref as PanelRef} from '../../../../state/views/panel/types';
import {type Ref as PanelBankConfigRef} from '../../../../state/views/panelBankConfig/types';
import {
  makeSectionMediaActualSizeSelector,
  makeSectionMediaAlwaysOriginalSelector,
  makeSectionMediaBoundingBoxConfigSelector,
  makeSectionMediaColumnCountSelector,
  makeSectionMediaCompareSettingsSelector,
  makeSectionMediaDisableVideoStreamSelector,
  makeSectionMediaFitToDimensionSelector,
  makeSectionMediaGallerySettingsSelector,
  makeSectionMediaGridSettingsSelector,
  makeSectionMediaMaxGalleryItemsSelector,
  makeSectionMediaMaxRunsSelector,
  makeSectionMediaMaxYAxisCountSelector,
  makeSectionMediaModeSelector,
  makeSectionMediaObject3DBackgroundColorSelector,
  makeSectionMediaPixelatedSelector,
  makeSectionMediaPointColorSelector,
  makeSectionMediaSegmentationMaskConfigSelector,
  makeSectionMediaSettingsSelector,
  makeSectionMediaSliderKeySelector,
  makeSectionMediaSliderSyncSelector,
  makeSectionMediaSnapToExistingStepSelector,
  makeSectionMediaStepStrideLengthSelector,
  makeSectionMediaUseRightHandedSystemSelector,
  makeSectionMediaVideoPlaybackSyncAutoplaySelector,
  makeSectionMediaVideoPlaybackSyncLoopSelector,
  makeSectionMediaVideoPlaybackSyncSelector,
  makeSectionMediaVideoPlayerTimeStepSelector,
} from '../../../../state/views/panelBankSectionConfig/selectors';
import {
  type PanelBankSectionConfigNormalized,
  type Ref as SectionRef,
} from '../../../../state/views/panelBankSectionConfig/types';
import {
  makeWorkspaceMediaActualSizeSelector,
  makeWorkspaceMediaAlwaysOriginalSelector,
  makeWorkspaceMediaBoundingBoxConfigSelector,
  makeWorkspaceMediaColumnCountSelector,
  makeWorkspaceMediaCompareSettingsSelector,
  makeWorkspaceMediaDisableVideoStreamSelector,
  makeWorkspaceMediaFitToDimensionSelector,
  makeWorkspaceMediaGallerySettingsSelector,
  makeWorkspaceMediaGridSettingsSelector,
  makeWorkspaceMediaMaxGalleryItemsSelector,
  makeWorkspaceMediaMaxRunsSelector,
  makeWorkspaceMediaMaxYAxisCountSelector,
  makeWorkspaceMediaModeSelector,
  makeWorkspaceMediaObject3DBackgroundColorSelector,
  makeWorkspaceMediaPixelatedSelector,
  makeWorkspaceMediaPointColorSelector,
  makeWorkspaceMediaSegmentationMaskConfigSelector,
  makeWorkspaceMediaSliderKeySelector,
  makeWorkspaceMediaSliderSyncSelector,
  makeWorkspaceMediaSnapToExistingStepSelector,
  makeWorkspaceMediaStepStrideLengthSelector,
  makeWorkspaceMediaUseRightHandedSystemSelector,
  makeWorkspaceMediaVideoPlaybackSyncSelector,
  makeWorkspaceMediaVideoPlayerTimeStepSelector,
  makeWorkspaceSettingsMediaSelector,
  makeWorkspaceSettingsVideoPlaybackSyncAutoplaySelector,
  makeWorkspaceSettingsVideoPlaybackSyncLoopSelector,
} from '../../../../state/views/workspaceSettings/selectors';
import {type Ref as WorkspaceSettingsRef} from '../../../../state/views/workspaceSettings/types';
import {type RootState} from '../../../../types/redux';
import {panelViews} from '../../../PanelBank/types';
import {type MediaBrowserPanelConfig} from '../../../PanelMediaBrowser';
import {type LayoutMode} from '../../../PanelMediaBrowser/common';
import {
  DEFAULT_MAX_Y_AXIS_COUNT,
  MAX_COL_COUNT,
  MIN_COL_COUNT,
} from '../../../PanelMediaBrowser/config';
import {defaultColumnCount} from '../../../PanelMediaBrowser/useMediaPanelConfig';
import {DEFAULT_LINE_PLOT_NUM_RUNS_NEW} from '../runLinePlots/maxRunDefaults';
import {type MediaSetting, type MediaSettingsTab, SettingsLevel} from './types';

export type MediaOverrideCounts = Partial<
  Record<keyof MediaBrowserPanelConfig, number>
>;

export type SectionWithRef = {
  ref: SectionRef;
  section: PanelBankSectionConfigNormalized;
};

export type MediaPanelConfigWithRef = {
  ref: PanelRef;
  config: MediaBrowserPanelConfig;
};

type SectionWithMediaSettings = {
  ref: SectionRef;
  sectionSettings: {media: MediaBrowserPanelConfig};
};

// declaring default values outside to avoid creating empty array each time the selector is called
const DEFAULT_SECTIONS_WITH_MEDIA_SETTINGS: SectionWithMediaSettings[] = [];
const DEFAULT_MEDIA_PANELS: MediaPanelConfigWithRef[] = [];

// =============================================================================
// Base State Slice Selectors
// =============================================================================
// Simple state accessors, no derived data so no need for createSelector

const selectPanelBankConfigSectionRefs =
  (panelBankConfigRef: PanelBankConfigRef) => (state: RootState) =>
    state.views.parts[panelBankConfigRef.type][panelBankConfigRef.id]
      .sectionRefs;

const selectPanelBankSections = (state: RootState) =>
  state.views.parts['panel-bank-section-config'];

const selectPanelParts = (state: RootState) => state.views.parts.panel;

export const selectPanel = (panelRef: PanelRef) => (state: RootState) =>
  state.views.parts[panelRef.type][panelRef.id];

export const selectCustomRunColors =
  (customRunColorsRef: CustomRunColorsRef) => (state: RootState) =>
    state.views.parts[customRunColorsRef.type][customRunColorsRef.id];

// =============================================================================
// Media Settings Selectors
// =============================================================================
// These selectors are used by MediaWorkspaceSettingsContextProvider to
// efficiently access sections and panels for override counting/clearing.
// They use createSelector for memoization, only recomputing when the
// underlying state slices change.

/**
 * Returns all sections that have media settings configured.
 * Used for counting and clearing section-level overrides.
 *
 * @param panelBankConfigRef - The ref to the panel bank config, or undefined
 * @returns Memoized selector returning sections with their media settings
 */
export const selectSectionsWithMediaSettings = (
  panelBankConfigRef: PanelBankConfigRef | undefined
): Selector<RootState, SectionWithMediaSettings[]> => {
  if (!panelBankConfigRef) {
    return () => DEFAULT_SECTIONS_WITH_MEDIA_SETTINGS;
  }

  return createSelector(
    selectPanelBankConfigSectionRefs(panelBankConfigRef),
    selectPanelBankSections,
    (sectionRefs, sections) => {
      const sectionsWithMediaSettings: SectionWithMediaSettings[] = [];
      for (const sectionRef of sectionRefs) {
        const mediaSettings =
          sections[sectionRef.id].sectionSettings?.media ?? {};

        // only look at sections where there are media settings fields actually set
        if (Object.keys(mediaSettings).length > 0) {
          sectionsWithMediaSettings.push({
            ref: sectionRef,
            sectionSettings: {
              media: mediaSettings,
            },
          });
        }
      }
      return sectionsWithMediaSettings;
    }
  );
};

/**
 * Selector factory that returns all media browser panels with their refs.
 * Used for workspace-level settings to track and clear panel overrides.
 *
 * @param panelBankConfigRef - The ref to the panel bank config, or undefined
 * @returns A memoized selector returning media panels with refs, or empty array if ref is undefined
 */
export const selectAllMediaPanelConfigsWithRefs = (
  panelBankConfigRef: PanelBankConfigRef | undefined
): Selector<RootState, MediaPanelConfigWithRef[]> => {
  if (!panelBankConfigRef) {
    return () => DEFAULT_MEDIA_PANELS;
  }

  return createSelector(
    selectPanelBankConfigSectionRefs(panelBankConfigRef),
    selectPanelBankSections,
    selectPanelParts,
    (sectionRefs, sections, panelParts) => {
      const mediaPanels: MediaPanelConfigWithRef[] = [];

      for (const sectionRef of sectionRefs) {
        for (const panelRef of sections[sectionRef.id].panelRefs) {
          const panel = panelParts[panelRef.id];
          if (panel.viewType === panelViews.mediaBrowser) {
            mediaPanels.push({
              ref: panelRef,
              config: panel.config as MediaBrowserPanelConfig,
            });
          }
        }
      }
      return mediaPanels;
    }
  );
};

/**
 * Returns all media browser panels within a specific section.
 * Used for counting and clearing panel-level overrides at the section level.
 *
 * @param sectionRef - The ref to the section
 * @returns Memoized selector returning media panels with their configs
 */
export const selectMediaPanelConfigsInSection = (
  sectionRef: SectionRef
): Selector<RootState, MediaPanelConfigWithRef[]> => {
  return createSelector(
    selectPanelBankSections,
    selectPanelParts,
    (sections, panelParts) => {
      const mediaPanels: MediaPanelConfigWithRef[] = [];
      const section = sections[sectionRef.id];

      if (!section) {
        return mediaPanels;
      }

      for (const panelRef of section.panelRefs) {
        const panel = panelParts[panelRef.id];
        if (panel.viewType === panelViews.mediaBrowser) {
          mediaPanels.push({
            ref: panelRef,
            config: panel.config as MediaBrowserPanelConfig,
          });
        }
      }
      return mediaPanels;
    }
  );
};

const SLIDER_SYNC_DEFAULT = false;

export const makeCascadingSectionMediaSliderSyncSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined
): Selector<RootState, boolean> => {
  return createSelector(
    makeWorkspaceMediaSliderSyncSelector(workspaceSettingsRef),
    makeSectionMediaSliderSyncSelector(sectionRef),
    (workspaceSliderSync, sectionSliderSync) =>
      // Lower-level settings always take precedence, even when they equal the
      // default (matching getCascadedMediaSettings spread semantics).
      (sectionSliderSync ?? workspaceSliderSync ?? SLIDER_SYNC_DEFAULT) === true
  );
};

export const makeMediaSliderSyncDisplaySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel
): Selector<RootState, boolean> => {
  return createSelector(
    makeWorkspaceMediaSliderSyncSelector(workspaceSettingsRef),
    makeCascadingSectionMediaSliderSyncSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    (workspaceSliderSync, sectionCascadingSliderSync) =>
      settingsLevel === SettingsLevel.SECTION
        ? sectionCascadingSliderSync
        : workspaceSliderSync === true
  );
};

const MEDIA_SLIDER_KEY_DEFAULT = '_step';

function getCascadingMediaSliderKey(
  workspaceMediaSliderKey: string | undefined,
  sectionMediaSliderKey: string | undefined
): string {
  // Lower-level settings always take precedence, even when they equal the
  // default (matching getCascadedMediaSettings spread semantics).
  return (
    sectionMediaSliderKey ?? workspaceMediaSliderKey ?? MEDIA_SLIDER_KEY_DEFAULT
  );
}

export const makeCascadingSectionMediaSliderKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined
): Selector<RootState, string> => {
  return createSelector(
    makeWorkspaceMediaSliderKeySelector(workspaceSettingsRef),
    makeSectionMediaSliderKeySelector(sectionRef),
    (workspaceMediaSliderKey, sectionMediaSliderKey) =>
      getCascadingMediaSliderKey(workspaceMediaSliderKey, sectionMediaSliderKey)
  );
};

/** Raw panel-level mediaSliderKey from the panel's own config (not cascaded). */
export const makePanelMediaSliderKeySelector = (
  panelRef: PanelRef | undefined
): Selector<RootState, string | undefined> => {
  if (panelRef == null) {
    return () => undefined;
  }
  return createSelector(selectPanel(panelRef), panel =>
    panel?.viewType === panelViews.mediaBrowser
      ? (panel.config as MediaBrowserPanelConfig).mediaSliderKey
      : undefined
  );
};

/** Workspace → section → panel cascaded value for panel-level display and behavior. */
export const makeCascadingPanelMediaSliderKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  panelRef: PanelRef | undefined
): Selector<RootState, string> => {
  return createSelector(
    makeCascadingSectionMediaSliderKeySelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makePanelMediaSliderKeySelector(panelRef),
    (sectionCascadingMediaSliderKey, panelMediaSliderKey) =>
      panelMediaSliderKey ?? sectionCascadingMediaSliderKey
  );
};

export const makeMediaSliderKeyDisplaySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, string> => {
  return createSelector(
    makeWorkspaceMediaSliderKeySelector(workspaceSettingsRef),
    makeCascadingSectionMediaSliderKeySelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makeCascadingPanelMediaSliderKeySelector(
      workspaceSettingsRef,
      sectionRef,
      panelRef
    ),
    (
      workspaceMediaSliderKey,
      sectionCascadingMediaSliderKey,
      panelCascadingMediaSliderKey
    ) => {
      switch (settingsLevel) {
        case SettingsLevel.PANEL:
          return panelCascadingMediaSliderKey;
        case SettingsLevel.SECTION:
          return sectionCascadingMediaSliderKey;
        default:
          return workspaceMediaSliderKey ?? MEDIA_SLIDER_KEY_DEFAULT;
      }
    }
  );
};

/**
 * Whether to show override clear rows for SliderKey — mirrors
 * `config.mediaSliderKey != null` on config with defaults, which always
 * resolves to a key.
 */
export const makeSliderKeyShowOverridesSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel
): Selector<RootState, boolean> => {
  return createSelector(
    makeMediaSliderKeyDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel
    ),
    mediaSliderKey => mediaSliderKey != null
  );
};

const MEDIA_MODE_DEFAULT: LayoutMode = 'gallery';

function getCascadingMediaMode(
  workspaceMode: LayoutMode | undefined,
  sectionMode: LayoutMode | undefined
): LayoutMode {
  return sectionMode ?? workspaceMode ?? MEDIA_MODE_DEFAULT;
}

export const makeCascadingSectionMediaModeSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined
): Selector<RootState, LayoutMode> => {
  return createSelector(
    makeWorkspaceMediaModeSelector(workspaceSettingsRef),
    makeSectionMediaModeSelector(sectionRef),
    (workspaceMode, sectionMode) =>
      getCascadingMediaMode(workspaceMode, sectionMode)
  );
};

/** Raw panel-level mode from the panel's own config (not cascaded). */
export const makePanelMediaModeSelector = (
  panelRef: PanelRef | undefined
): Selector<RootState, LayoutMode | undefined> => {
  if (panelRef == null) {
    return () => undefined;
  }
  return createSelector(selectPanel(panelRef), panel =>
    panel?.viewType === panelViews.mediaBrowser
      ? (panel.config as MediaBrowserPanelConfig).mode
      : undefined
  );
};

/** Workspace → section → panel cascaded value for panel-level display and behavior. */
export const makeCascadingPanelMediaModeSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  panelRef: PanelRef | undefined
): Selector<RootState, LayoutMode> => {
  return createSelector(
    makeCascadingSectionMediaModeSelector(workspaceSettingsRef, sectionRef),
    makePanelMediaModeSelector(panelRef),
    (sectionCascadingMode, panelMode) => panelMode ?? sectionCascadingMode
  );
};

export const makeMediaModeDisplaySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, LayoutMode> => {
  return createSelector(
    makeWorkspaceMediaModeSelector(workspaceSettingsRef),
    makeCascadingSectionMediaModeSelector(workspaceSettingsRef, sectionRef),
    makeCascadingPanelMediaModeSelector(
      workspaceSettingsRef,
      sectionRef,
      panelRef
    ),
    (workspaceMode, sectionCascadingMode, panelCascadingMode) => {
      switch (settingsLevel) {
        case SettingsLevel.PANEL:
          return panelCascadingMode;
        case SettingsLevel.SECTION:
          return sectionCascadingMode;
        default:
          return workspaceMode ?? MEDIA_MODE_DEFAULT;
      }
    }
  );
};

/**
 * Whether to show override clear rows for LayoutMode — mirrors
 * `config.mode != null` on config with defaults, which always resolves to a
 * mode.
 */
export const makeLayoutModeShowOverridesSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> => {
  return createSelector(
    makeMediaModeDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    mode => mode != null
  );
};

/**
 * Component remount key for LayoutMode — mirrors getComponentKey on cascaded config.
 */
export const makeLayoutModeComponentKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, string> => {
  return createSelector(
    makeMediaModeDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    mode => `${mode}-new-layout`
  );
};

const SNAP_TO_EXISTING_STEP_DEFAULT = true;

function getCascadingSnapToExistingStep(
  workspaceSnapToExistingStep: boolean | undefined,
  sectionSnapToExistingStep: boolean | undefined
): boolean {
  return (
    sectionSnapToExistingStep ??
    workspaceSnapToExistingStep ??
    SNAP_TO_EXISTING_STEP_DEFAULT
  );
}

export const makeCascadingSectionMediaSnapToExistingStepSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined
): Selector<RootState, boolean> => {
  return createSelector(
    makeWorkspaceMediaSnapToExistingStepSelector(workspaceSettingsRef),
    makeSectionMediaSnapToExistingStepSelector(sectionRef),
    (workspaceSnapToExistingStep, sectionSnapToExistingStep) =>
      getCascadingSnapToExistingStep(
        workspaceSnapToExistingStep,
        sectionSnapToExistingStep
      )
  );
};

/** Raw panel-level snapToExistingStep from the panel's own config (not cascaded). */
export const makePanelMediaSnapToExistingStepSelector = (
  panelRef: PanelRef | undefined
): Selector<RootState, boolean | undefined> => {
  if (panelRef == null) {
    return () => undefined;
  }
  return createSelector(selectPanel(panelRef), panel =>
    panel?.viewType === panelViews.mediaBrowser
      ? (panel.config as MediaBrowserPanelConfig).snapToExistingStep
      : undefined
  );
};

/** Workspace → section → panel cascaded value for panel-level display and behavior. */
export const makeCascadingPanelMediaSnapToExistingStepSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  panelRef: PanelRef | undefined
): Selector<RootState, boolean> => {
  return createSelector(
    makeCascadingSectionMediaSnapToExistingStepSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makePanelMediaSnapToExistingStepSelector(panelRef),
    (sectionCascadingSnapToExistingStep, panelSnapToExistingStep) =>
      panelSnapToExistingStep ?? sectionCascadingSnapToExistingStep
  );
};

export const makeMediaSnapToExistingStepDisplaySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> => {
  return createSelector(
    makeWorkspaceMediaSnapToExistingStepSelector(workspaceSettingsRef),
    makeCascadingSectionMediaSnapToExistingStepSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makeCascadingPanelMediaSnapToExistingStepSelector(
      workspaceSettingsRef,
      sectionRef,
      panelRef
    ),
    (
      workspaceSnapToExistingStep,
      sectionCascadingSnapToExistingStep,
      panelCascadingSnapToExistingStep
    ) => {
      switch (settingsLevel) {
        case SettingsLevel.PANEL:
          return panelCascadingSnapToExistingStep;
        case SettingsLevel.SECTION:
          return sectionCascadingSnapToExistingStep;
        default:
          return workspaceSnapToExistingStep ?? SNAP_TO_EXISTING_STEP_DEFAULT;
      }
    }
  );
};

/**
 * Whether to show override clear rows for SnapToExistingStep — mirrors
 * `config.snapToExistingStep != null` on config with defaults, which always
 * resolves to a value.
 */
export const makeSnapToExistingStepShowOverridesSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> => {
  return createSelector(
    makeMediaSnapToExistingStepDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    snapToExistingStep => snapToExistingStep != null
  );
};

/**
 * Component remount key for SnapToExistingStep — mirrors getComponentKey on cascaded config.
 */
export const makeSnapToExistingStepComponentKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, string> => {
  return createSelector(
    makeMediaSnapToExistingStepDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    snapToExistingStep => `${snapToExistingStep.toString()}`
  );
};

function getCascadingMaxRuns(
  workspaceMaxRuns: number | undefined,
  sectionMaxRuns: number | undefined
): number {
  return sectionMaxRuns ?? workspaceMaxRuns ?? DEFAULT_LINE_PLOT_NUM_RUNS_NEW;
}

export const makeCascadingSectionMediaMaxRunsSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined
): Selector<RootState, number> => {
  return createSelector(
    makeWorkspaceMediaMaxRunsSelector(workspaceSettingsRef),
    makeSectionMediaMaxRunsSelector(sectionRef),
    (workspaceMaxRuns, sectionMaxRuns) =>
      getCascadingMaxRuns(workspaceMaxRuns, sectionMaxRuns)
  );
};

/** Raw panel-level maxRuns from the panel's own config (not cascaded). */
export const makePanelMediaMaxRunsSelector = (
  panelRef: PanelRef | undefined
): Selector<RootState, number | undefined> => {
  if (panelRef == null) {
    return () => undefined;
  }
  return createSelector(selectPanel(panelRef), panel =>
    panel?.viewType === panelViews.mediaBrowser
      ? (panel.config as MediaBrowserPanelConfig).maxRuns
      : undefined
  );
};

/** Workspace → section → panel cascaded value for panel-level display and behavior. */
export const makeCascadingPanelMediaMaxRunsSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  panelRef: PanelRef | undefined
): Selector<RootState, number> => {
  return createSelector(
    makeCascadingSectionMediaMaxRunsSelector(workspaceSettingsRef, sectionRef),
    makePanelMediaMaxRunsSelector(panelRef),
    (sectionCascadingMaxRuns, panelMaxRuns) =>
      panelMaxRuns ?? sectionCascadingMaxRuns
  );
};

export const makeMediaMaxRunsDisplaySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, number> => {
  return createSelector(
    makeWorkspaceMediaMaxRunsSelector(workspaceSettingsRef),
    makeCascadingSectionMediaMaxRunsSelector(workspaceSettingsRef, sectionRef),
    makeCascadingPanelMediaMaxRunsSelector(
      workspaceSettingsRef,
      sectionRef,
      panelRef
    ),
    (workspaceMaxRuns, sectionCascadingMaxRuns, panelCascadingMaxRuns) => {
      switch (settingsLevel) {
        case SettingsLevel.PANEL:
          return panelCascadingMaxRuns;
        case SettingsLevel.SECTION:
          return sectionCascadingMaxRuns;
        default:
          return workspaceMaxRuns ?? DEFAULT_LINE_PLOT_NUM_RUNS_NEW;
      }
    }
  );
};

/**
 * Whether to show override clear rows for MaxRuns — mirrors
 * `config.maxRuns != null` on config with defaults, which always
 * resolves to a value.
 */
export const makeMaxRunsShowOverridesSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> => {
  return createSelector(
    makeMediaMaxRunsDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    maxRuns => maxRuns != null
  );
};

/**
 * Component remount key for MaxRuns — mirrors getComponentKey on cascaded config.
 */
export const makeMaxRunsComponentKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, string> => {
  return createSelector(
    makeMediaMaxRunsDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    maxRuns => `${maxRuns}`
  );
};

export const makeCascadingSectionMediaMaxGalleryItemsSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined
): Selector<RootState, number | undefined> => {
  return createSelector(
    makeWorkspaceMediaMaxGalleryItemsSelector(workspaceSettingsRef),
    makeSectionMediaMaxGalleryItemsSelector(sectionRef),
    (workspaceMaxGalleryItems, sectionMaxGalleryItems) =>
      sectionMaxGalleryItems ?? workspaceMaxGalleryItems
  );
};

/** Raw panel-level maxGalleryItems from the panel's own config (not cascaded). */
export const makePanelMediaMaxGalleryItemsSelector = (
  panelRef: PanelRef | undefined
): Selector<RootState, number | undefined> => {
  if (panelRef == null) {
    return () => undefined;
  }
  return createSelector(selectPanel(panelRef), panel =>
    panel?.viewType === panelViews.mediaBrowser
      ? (panel.config as MediaBrowserPanelConfig).maxGalleryItems
      : undefined
  );
};

/** Workspace → section → panel cascaded value for panel-level display and behavior. */
export const makeCascadingPanelMediaMaxGalleryItemsSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  panelRef: PanelRef | undefined
): Selector<RootState, number | undefined> => {
  return createSelector(
    makeCascadingSectionMediaMaxGalleryItemsSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makePanelMediaMaxGalleryItemsSelector(panelRef),
    (sectionCascadingMaxGalleryItems, panelMaxGalleryItems) =>
      panelMaxGalleryItems ?? sectionCascadingMaxGalleryItems
  );
};

export const makeMediaMaxGalleryItemsDisplaySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, number | undefined> => {
  return createSelector(
    makeWorkspaceMediaMaxGalleryItemsSelector(workspaceSettingsRef),
    makeCascadingSectionMediaMaxGalleryItemsSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makeCascadingPanelMediaMaxGalleryItemsSelector(
      workspaceSettingsRef,
      sectionRef,
      panelRef
    ),
    (
      workspaceMaxGalleryItems,
      sectionCascadingMaxGalleryItems,
      panelCascadingMaxGalleryItems
    ) => {
      switch (settingsLevel) {
        case SettingsLevel.PANEL:
          return panelCascadingMaxGalleryItems;
        case SettingsLevel.SECTION:
          return sectionCascadingMaxGalleryItems;
        default:
          return workspaceMaxGalleryItems;
      }
    }
  );
};

/**
 * Whether to show override clear rows for MaxItemCount — mirrors
 * `config.maxGalleryItems != null` on the cascaded config.
 */
export const makeMaxGalleryItemsShowOverridesSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> => {
  return createSelector(
    makeMediaMaxGalleryItemsDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    maxGalleryItems => maxGalleryItems != null
  );
};

/**
 * Component remount key for MaxItemCount — mirrors getComponentKey on cascaded config.
 */
export const makeMaxGalleryItemsComponentKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, string> => {
  return createSelector(
    makeMediaMaxGalleryItemsDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    maxGalleryItems => `${maxGalleryItems?.toString()}`
  );
};

function getCascadingMaxYAxisCount(
  workspaceMaxYAxisCount: number | undefined,
  sectionMaxYAxisCount: number | undefined
): number {
  return (
    sectionMaxYAxisCount ?? workspaceMaxYAxisCount ?? DEFAULT_MAX_Y_AXIS_COUNT
  );
}

export const makeCascadingSectionMediaMaxYAxisCountSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined
): Selector<RootState, number> => {
  return createSelector(
    makeWorkspaceMediaMaxYAxisCountSelector(workspaceSettingsRef),
    makeSectionMediaMaxYAxisCountSelector(sectionRef),
    (workspaceMaxYAxisCount, sectionMaxYAxisCount) =>
      getCascadingMaxYAxisCount(workspaceMaxYAxisCount, sectionMaxYAxisCount)
  );
};

/** Raw panel-level maxYAxisCount from the panel's own config (not cascaded). */
export const makePanelMediaMaxYAxisCountSelector = (
  panelRef: PanelRef | undefined
): Selector<RootState, number | undefined> => {
  if (panelRef == null) {
    return () => undefined;
  }
  return createSelector(selectPanel(panelRef), panel =>
    panel?.viewType === panelViews.mediaBrowser
      ? (panel.config as MediaBrowserPanelConfig).maxYAxisCount
      : undefined
  );
};

/** Workspace → section → panel cascaded value for panel-level display and behavior. */
export const makeCascadingPanelMediaMaxYAxisCountSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  panelRef: PanelRef | undefined
): Selector<RootState, number> => {
  return createSelector(
    makeCascadingSectionMediaMaxYAxisCountSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makePanelMediaMaxYAxisCountSelector(panelRef),
    (sectionCascadingMaxYAxisCount, panelMaxYAxisCount) =>
      panelMaxYAxisCount ?? sectionCascadingMaxYAxisCount
  );
};

export const makeMediaMaxYAxisCountDisplaySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, number> => {
  return createSelector(
    makeWorkspaceMediaMaxYAxisCountSelector(workspaceSettingsRef),
    makeCascadingSectionMediaMaxYAxisCountSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makeCascadingPanelMediaMaxYAxisCountSelector(
      workspaceSettingsRef,
      sectionRef,
      panelRef
    ),
    (
      workspaceMaxYAxisCount,
      sectionCascadingMaxYAxisCount,
      panelCascadingMaxYAxisCount
    ) => {
      switch (settingsLevel) {
        case SettingsLevel.PANEL:
          return panelCascadingMaxYAxisCount;
        case SettingsLevel.SECTION:
          return sectionCascadingMaxYAxisCount;
        default:
          return workspaceMaxYAxisCount ?? DEFAULT_MAX_Y_AXIS_COUNT;
      }
    }
  );
};

/**
 * Whether to show override clear rows for MaxRowCount — mirrors
 * `config.maxYAxisCount != null` on config with defaults, which always
 * resolves to a value.
 */
export const makeMaxYAxisCountShowOverridesSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> => {
  return createSelector(
    makeMediaMaxYAxisCountDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    maxYAxisCount => maxYAxisCount != null
  );
};

/**
 * Component remount key for MaxRowCount — mirrors getComponentKey on cascaded config.
 */
export const makeMaxYAxisCountComponentKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, string> => {
  return createSelector(
    makeMediaMaxYAxisCountDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    maxYAxisCount => `${maxYAxisCount.toString()}`
  );
};

export const makeCascadingSectionMediaColumnCountSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined
): Selector<RootState, number | undefined> => {
  return createSelector(
    makeWorkspaceMediaColumnCountSelector(workspaceSettingsRef),
    makeSectionMediaColumnCountSelector(sectionRef),
    (workspaceColumnCount, sectionColumnCount) =>
      sectionColumnCount ?? workspaceColumnCount
  );
};

/** Raw panel-level columnCount from the panel's own config (not cascaded). */
export const makePanelMediaColumnCountSelector = (
  panelRef: PanelRef | undefined
): Selector<RootState, number | undefined> => {
  if (panelRef == null) {
    return () => undefined;
  }
  return createSelector(selectPanel(panelRef), panel =>
    panel?.viewType === panelViews.mediaBrowser
      ? (panel.config as MediaBrowserPanelConfig).columnCount
      : undefined
  );
};

/** Workspace → section → panel cascaded value for panel-level display and behavior. */
export const makeCascadingPanelMediaColumnCountSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  panelRef: PanelRef | undefined
): Selector<RootState, number | undefined> => {
  return createSelector(
    makeCascadingSectionMediaColumnCountSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makePanelMediaColumnCountSelector(panelRef),
    (sectionCascadingColumnCount, panelColumnCount) =>
      panelColumnCount ?? sectionCascadingColumnCount
  );
};

export const makeMediaColumnCountDisplaySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, number> => {
  return createSelector(
    makeWorkspaceMediaColumnCountSelector(workspaceSettingsRef),
    makeCascadingSectionMediaColumnCountSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makeCascadingPanelMediaColumnCountSelector(
      workspaceSettingsRef,
      sectionRef,
      panelRef
    ),
    makeMediaModeDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    (
      workspaceColumnCount,
      sectionCascadingColumnCount,
      panelCascadingColumnCount,
      mode
    ) => {
      let columnCount: number | undefined;
      switch (settingsLevel) {
        case SettingsLevel.PANEL:
          columnCount = panelCascadingColumnCount;
          break;
        case SettingsLevel.SECTION:
          columnCount = sectionCascadingColumnCount;
          break;
        default:
          columnCount = workspaceColumnCount;
      }
      return columnCount ?? defaultColumnCount(undefined, mode);
    }
  );
};

/**
 * Whether to show override clear rows for NumberOfColumns — mirrors
 * `config.columnCount != null` on cascaded config.
 */
export const makeColumnCountShowOverridesSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> => {
  return createSelector(
    makeMediaColumnCountDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    columnCount => columnCount != null
  );
};

/**
 * Component remount key for NumberOfColumns — mirrors getComponentKey on cascaded config.
 */
export const makeColumnCountComponentKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, string> => {
  return createSelector(
    makeMediaColumnCountDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    columnCount => `${columnCount.toString()}`
  );
};

const ACTUAL_SIZE_DEFAULT = false;

function getCascadingActualSize(
  workspaceActualSize: boolean | undefined,
  sectionActualSize: boolean | undefined
): boolean {
  return sectionActualSize ?? workspaceActualSize ?? ACTUAL_SIZE_DEFAULT;
}

export const makeCascadingSectionMediaActualSizeSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined
): Selector<RootState, boolean> => {
  return createSelector(
    makeWorkspaceMediaActualSizeSelector(workspaceSettingsRef),
    makeSectionMediaActualSizeSelector(sectionRef),
    (workspaceActualSize, sectionActualSize) =>
      getCascadingActualSize(workspaceActualSize, sectionActualSize)
  );
};

/** Raw panel-level actualSize from the panel's own config (not cascaded). */
export const makePanelMediaActualSizeSelector = (
  panelRef: PanelRef | undefined
): Selector<RootState, boolean | undefined> => {
  if (panelRef == null) {
    return () => undefined;
  }
  return createSelector(selectPanel(panelRef), panel =>
    panel?.viewType === panelViews.mediaBrowser
      ? (panel.config as MediaBrowserPanelConfig).actualSize
      : undefined
  );
};

/** Workspace → section → panel cascaded value for panel-level display and behavior. */
export const makeCascadingPanelMediaActualSizeSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  panelRef: PanelRef | undefined
): Selector<RootState, boolean> => {
  return createSelector(
    makeCascadingSectionMediaActualSizeSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makePanelMediaActualSizeSelector(panelRef),
    (sectionCascadingActualSize, panelActualSize) =>
      panelActualSize ?? sectionCascadingActualSize
  );
};

export const makeMediaActualSizeDisplaySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> => {
  return createSelector(
    makeWorkspaceMediaActualSizeSelector(workspaceSettingsRef),
    makeCascadingSectionMediaActualSizeSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makeCascadingPanelMediaActualSizeSelector(
      workspaceSettingsRef,
      sectionRef,
      panelRef
    ),
    (
      workspaceActualSize,
      sectionCascadingActualSize,
      panelCascadingActualSize
    ) => {
      switch (settingsLevel) {
        case SettingsLevel.PANEL:
          return panelCascadingActualSize;
        case SettingsLevel.SECTION:
          return sectionCascadingActualSize;
        default:
          return workspaceActualSize ?? ACTUAL_SIZE_DEFAULT;
      }
    }
  );
};

const FIT_TO_DIMENSION_DEFAULT = false;

function getCascadingFitToDimension(
  workspaceFitToDimension: boolean | undefined,
  sectionFitToDimension: boolean | undefined
): boolean {
  return (
    sectionFitToDimension ?? workspaceFitToDimension ?? FIT_TO_DIMENSION_DEFAULT
  );
}

export const makeCascadingSectionMediaFitToDimensionSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined
): Selector<RootState, boolean> => {
  return createSelector(
    makeWorkspaceMediaFitToDimensionSelector(workspaceSettingsRef),
    makeSectionMediaFitToDimensionSelector(sectionRef),
    (workspaceFitToDimension, sectionFitToDimension) =>
      getCascadingFitToDimension(workspaceFitToDimension, sectionFitToDimension)
  );
};

/** Raw panel-level fitToDimension from the panel's own config (not cascaded). */
export const makePanelMediaFitToDimensionSelector = (
  panelRef: PanelRef | undefined
): Selector<RootState, boolean | undefined> => {
  if (panelRef == null) {
    return () => undefined;
  }
  return createSelector(selectPanel(panelRef), panel =>
    panel?.viewType === panelViews.mediaBrowser
      ? (panel.config as MediaBrowserPanelConfig).fitToDimension
      : undefined
  );
};

/** Workspace → section → panel cascaded value for panel-level display and behavior. */
export const makeCascadingPanelMediaFitToDimensionSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  panelRef: PanelRef | undefined
): Selector<RootState, boolean> => {
  return createSelector(
    makeCascadingSectionMediaFitToDimensionSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makePanelMediaFitToDimensionSelector(panelRef),
    (sectionCascadingFitToDimension, panelFitToDimension) =>
      panelFitToDimension ?? sectionCascadingFitToDimension
  );
};

export const makeMediaFitToDimensionDisplaySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> => {
  return createSelector(
    makeWorkspaceMediaFitToDimensionSelector(workspaceSettingsRef),
    makeCascadingSectionMediaFitToDimensionSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makeCascadingPanelMediaFitToDimensionSelector(
      workspaceSettingsRef,
      sectionRef,
      panelRef
    ),
    (
      workspaceFitToDimension,
      sectionCascadingFitToDimension,
      panelCascadingFitToDimension
    ) => {
      switch (settingsLevel) {
        case SettingsLevel.PANEL:
          return panelCascadingFitToDimension;
        case SettingsLevel.SECTION:
          return sectionCascadingFitToDimension;
        default:
          return workspaceFitToDimension ?? FIT_TO_DIMENSION_DEFAULT;
      }
    }
  );
};

/**
 * Whether to show override clear rows for OriginalSize — mirrors
 * `config.actualSize != null` on config with defaults, which always
 * resolves to a value.
 */
export const makeOriginalSizeShowOverridesSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> => {
  return createSelector(
    makeMediaActualSizeDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    actualSize => actualSize != null
  );
};

/**
 * Component remount key for OriginalSize — mirrors getComponentKey on cascaded
 * config. actualSize and fitToDimension are interdependent, so the component
 * re-mounts whenever either of them changes.
 */
export const makeOriginalSizeComponentKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, string> => {
  return createSelector(
    makeMediaActualSizeDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    makeMediaFitToDimensionDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    (actualSize, fitToDimension) =>
      `${actualSize.toString()},${fitToDimension.toString()}`
  );
};

const ALWAYS_ORIGINAL_DEFAULT = false;

function getCascadingAlwaysOriginal(
  workspaceAlwaysOriginal: boolean | undefined,
  sectionAlwaysOriginal: boolean | undefined
): boolean {
  return (
    sectionAlwaysOriginal ?? workspaceAlwaysOriginal ?? ALWAYS_ORIGINAL_DEFAULT
  );
}

export const makeCascadingSectionMediaAlwaysOriginalSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined
): Selector<RootState, boolean> => {
  return createSelector(
    makeWorkspaceMediaAlwaysOriginalSelector(workspaceSettingsRef),
    makeSectionMediaAlwaysOriginalSelector(sectionRef),
    (workspaceAlwaysOriginal, sectionAlwaysOriginal) =>
      getCascadingAlwaysOriginal(workspaceAlwaysOriginal, sectionAlwaysOriginal)
  );
};

/** Raw panel-level alwaysOriginal from the panel's own config (not cascaded). */
export const makePanelMediaAlwaysOriginalSelector = (
  panelRef: PanelRef | undefined
): Selector<RootState, boolean | undefined> => {
  if (panelRef == null) {
    return () => undefined;
  }
  return createSelector(selectPanel(panelRef), panel =>
    panel?.viewType === panelViews.mediaBrowser
      ? (panel.config as MediaBrowserPanelConfig).alwaysOriginal
      : undefined
  );
};

/** Workspace → section → panel cascaded value for panel-level display and behavior. */
export const makeCascadingPanelMediaAlwaysOriginalSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  panelRef: PanelRef | undefined
): Selector<RootState, boolean> => {
  return createSelector(
    makeCascadingSectionMediaAlwaysOriginalSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makePanelMediaAlwaysOriginalSelector(panelRef),
    (sectionCascadingAlwaysOriginal, panelAlwaysOriginal) =>
      panelAlwaysOriginal ?? sectionCascadingAlwaysOriginal
  );
};

export const makeMediaAlwaysOriginalDisplaySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> => {
  return createSelector(
    makeWorkspaceMediaAlwaysOriginalSelector(workspaceSettingsRef),
    makeCascadingSectionMediaAlwaysOriginalSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makeCascadingPanelMediaAlwaysOriginalSelector(
      workspaceSettingsRef,
      sectionRef,
      panelRef
    ),
    (
      workspaceAlwaysOriginal,
      sectionCascadingAlwaysOriginal,
      panelCascadingAlwaysOriginal
    ) => {
      switch (settingsLevel) {
        case SettingsLevel.PANEL:
          return panelCascadingAlwaysOriginal;
        case SettingsLevel.SECTION:
          return sectionCascadingAlwaysOriginal;
        default:
          return workspaceAlwaysOriginal ?? ALWAYS_ORIGINAL_DEFAULT;
      }
    }
  );
};

/**
 * Component remount key for ImageFitAndResolution — mirrors getComponentKey on
 * cascaded config. The three image fit settings are mutually exclusive, so the
 * component re-mounts whenever any of them changes.
 */
export const makeImageFitAndResolutionComponentKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, string> => {
  return createSelector(
    makeMediaActualSizeDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    makeMediaFitToDimensionDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    makeMediaAlwaysOriginalDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    (actualSize, fitToDimension, alwaysOriginal) =>
      `${actualSize?.toString()},${fitToDimension?.toString()},${alwaysOriginal?.toString()}`
  );
};

type GallerySettings = NonNullable<MediaBrowserPanelConfig['gallerySettings']>;

const GALLERY_SETTINGS_DEFAULT_SINGLE_RUN: GallerySettings = {axis: 'index'};
const GALLERY_SETTINGS_DEFAULT_MULTI_RUN: GallerySettings = {axis: 'run'};

function getGallerySettingsDefault(isSingleRun: boolean): GallerySettings {
  return isSingleRun
    ? GALLERY_SETTINGS_DEFAULT_SINGLE_RUN
    : GALLERY_SETTINGS_DEFAULT_MULTI_RUN;
}

function getCascadingGallerySettings(
  workspaceGallerySettings: GallerySettings | undefined,
  sectionGallerySettings: GallerySettings | undefined,
  isSingleRun: boolean
): GallerySettings {
  return (
    sectionGallerySettings ??
    workspaceGallerySettings ??
    getGallerySettingsDefault(isSingleRun)
  );
}

export const makeCascadingSectionMediaGallerySettingsSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  isSingleRun: boolean
): Selector<RootState, GallerySettings> => {
  return createSelector(
    makeWorkspaceMediaGallerySettingsSelector(workspaceSettingsRef),
    makeSectionMediaGallerySettingsSelector(sectionRef),
    (workspaceGallerySettings, sectionGallerySettings) =>
      getCascadingGallerySettings(
        workspaceGallerySettings,
        sectionGallerySettings,
        isSingleRun
      )
  );
};

/** Raw panel-level gallerySettings from the panel's own config (not cascaded). */
export const makePanelMediaGallerySettingsSelector = (
  panelRef: PanelRef | undefined
): Selector<RootState, GallerySettings | undefined> => {
  if (panelRef == null) {
    return () => undefined;
  }
  return createSelector(selectPanel(panelRef), panel =>
    panel?.viewType === panelViews.mediaBrowser
      ? (panel.config as MediaBrowserPanelConfig).gallerySettings
      : undefined
  );
};

/** Workspace → section → panel cascaded value for panel-level display and behavior. */
export const makeCascadingPanelMediaGallerySettingsSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  panelRef: PanelRef | undefined,
  isSingleRun: boolean
): Selector<RootState, GallerySettings> => {
  return createSelector(
    makeCascadingSectionMediaGallerySettingsSelector(
      workspaceSettingsRef,
      sectionRef,
      isSingleRun
    ),
    makePanelMediaGallerySettingsSelector(panelRef),
    (sectionCascadingGallerySettings, panelGallerySettings) =>
      panelGallerySettings ?? sectionCascadingGallerySettings
  );
};

export const makeMediaGallerySettingsDisplaySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef: PanelRef | undefined,
  isSingleRun: boolean
): Selector<RootState, GallerySettings> => {
  return createSelector(
    makeWorkspaceMediaGallerySettingsSelector(workspaceSettingsRef),
    makeCascadingSectionMediaGallerySettingsSelector(
      workspaceSettingsRef,
      sectionRef,
      isSingleRun
    ),
    makeCascadingPanelMediaGallerySettingsSelector(
      workspaceSettingsRef,
      sectionRef,
      panelRef,
      isSingleRun
    ),
    (
      workspaceGallerySettings,
      sectionCascadingGallerySettings,
      panelCascadingGallerySettings
    ) => {
      switch (settingsLevel) {
        case SettingsLevel.PANEL:
          return panelCascadingGallerySettings;
        case SettingsLevel.SECTION:
          return sectionCascadingGallerySettings;
        default:
          return (
            workspaceGallerySettings ?? getGallerySettingsDefault(isSingleRun)
          );
      }
    }
  );
};

type GridSettings = NonNullable<MediaBrowserPanelConfig['gridSettings']>;

const GRID_SETTINGS_DEFAULT_SINGLE_RUN: GridSettings = {
  xAxis: 'step',
  yAxis: 'index',
};
const GRID_SETTINGS_DEFAULT_MULTI_RUN: GridSettings = {
  xAxis: 'step',
  yAxis: 'run',
};

function getGridSettingsDefault(isSingleRun: boolean): GridSettings {
  return isSingleRun
    ? GRID_SETTINGS_DEFAULT_SINGLE_RUN
    : GRID_SETTINGS_DEFAULT_MULTI_RUN;
}

function getCascadingGridSettings(
  workspaceGridSettings: GridSettings | undefined,
  sectionGridSettings: GridSettings | undefined,
  isSingleRun: boolean
): GridSettings {
  return (
    sectionGridSettings ??
    workspaceGridSettings ??
    getGridSettingsDefault(isSingleRun)
  );
}

export const makeCascadingSectionMediaGridSettingsSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  isSingleRun: boolean
): Selector<RootState, GridSettings> => {
  return createSelector(
    makeWorkspaceMediaGridSettingsSelector(workspaceSettingsRef),
    makeSectionMediaGridSettingsSelector(sectionRef),
    (workspaceGridSettings, sectionGridSettings) =>
      getCascadingGridSettings(
        workspaceGridSettings,
        sectionGridSettings,
        isSingleRun
      )
  );
};

/** Raw panel-level gridSettings from the panel's own config (not cascaded). */
export const makePanelMediaGridSettingsSelector = (
  panelRef: PanelRef | undefined
): Selector<RootState, GridSettings | undefined> => {
  if (panelRef == null) {
    return () => undefined;
  }
  return createSelector(selectPanel(panelRef), panel =>
    panel?.viewType === panelViews.mediaBrowser
      ? (panel.config as MediaBrowserPanelConfig).gridSettings
      : undefined
  );
};

/** Workspace → section → panel cascaded value for panel-level display and behavior. */
export const makeCascadingPanelMediaGridSettingsSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  panelRef: PanelRef | undefined,
  isSingleRun: boolean
): Selector<RootState, GridSettings> => {
  return createSelector(
    makeCascadingSectionMediaGridSettingsSelector(
      workspaceSettingsRef,
      sectionRef,
      isSingleRun
    ),
    makePanelMediaGridSettingsSelector(panelRef),
    (sectionCascadingGridSettings, panelGridSettings) =>
      panelGridSettings ?? sectionCascadingGridSettings
  );
};

export const makeMediaGridSettingsDisplaySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef: PanelRef | undefined,
  isSingleRun: boolean
): Selector<RootState, GridSettings> => {
  return createSelector(
    makeWorkspaceMediaGridSettingsSelector(workspaceSettingsRef),
    makeCascadingSectionMediaGridSettingsSelector(
      workspaceSettingsRef,
      sectionRef,
      isSingleRun
    ),
    makeCascadingPanelMediaGridSettingsSelector(
      workspaceSettingsRef,
      sectionRef,
      panelRef,
      isSingleRun
    ),
    (
      workspaceGridSettings,
      sectionCascadingGridSettings,
      panelCascadingGridSettings
    ) => {
      switch (settingsLevel) {
        case SettingsLevel.PANEL:
          return panelCascadingGridSettings;
        case SettingsLevel.SECTION:
          return sectionCascadingGridSettings;
        default:
          return workspaceGridSettings ?? getGridSettingsDefault(isSingleRun);
      }
    }
  );
};

/**
 * Component remount key for the grid axis dropdowns — mirrors getComponentKey
 * on cascaded config. The two axes are interdependent since they swap when the
 * same axis is selected for both.
 */
export const makeGridSettingsComponentKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef,
  isSingleRun = false
): Selector<RootState, string> => {
  return createSelector(
    makeMediaGridSettingsDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef,
      isSingleRun
    ),
    gridSettings => `${gridSettings.xAxis},${gridSettings.yAxis}`
  );
};

const VIDEO_PLAYBACK_SYNC_DEFAULT = false;

/**
 * videoPlaybackSync cascades in reverse: the higher level takes precedence,
 * so a workspace-level value overrides section- and panel-level values.
 */
export const makeCascadingSectionMediaVideoPlaybackSyncSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined
): Selector<RootState, boolean> => {
  return createSelector(
    makeWorkspaceMediaVideoPlaybackSyncSelector(workspaceSettingsRef),
    makeSectionMediaVideoPlaybackSyncSelector(sectionRef),
    (workspaceVideoPlaybackSync, sectionVideoPlaybackSync) =>
      workspaceVideoPlaybackSync ??
      sectionVideoPlaybackSync ??
      VIDEO_PLAYBACK_SYNC_DEFAULT
  );
};

/** Raw panel-level videoPlaybackSync from the panel's own config (not cascaded). */
export const makePanelMediaVideoPlaybackSyncSelector = (
  panelRef: PanelRef | undefined
): Selector<RootState, boolean | undefined> => {
  if (panelRef == null) {
    return () => undefined;
  }
  return createSelector(selectPanel(panelRef), panel =>
    panel?.viewType === panelViews.mediaBrowser
      ? (panel.config as MediaBrowserPanelConfig).videoPlaybackSync
      : undefined
  );
};

/** Workspace → section → panel cascaded value; higher levels take precedence. */
export const makeCascadingPanelMediaVideoPlaybackSyncSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  panelRef: PanelRef | undefined
): Selector<RootState, boolean> => {
  return createSelector(
    makeWorkspaceMediaVideoPlaybackSyncSelector(workspaceSettingsRef),
    makeSectionMediaVideoPlaybackSyncSelector(sectionRef),
    makePanelMediaVideoPlaybackSyncSelector(panelRef),
    (
      workspaceVideoPlaybackSync,
      sectionVideoPlaybackSync,
      panelVideoPlaybackSync
    ) =>
      workspaceVideoPlaybackSync ??
      sectionVideoPlaybackSync ??
      panelVideoPlaybackSync ??
      VIDEO_PLAYBACK_SYNC_DEFAULT
  );
};

export const makeMediaVideoPlaybackSyncDisplaySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> => {
  return createSelector(
    makeWorkspaceMediaVideoPlaybackSyncSelector(workspaceSettingsRef),
    makeCascadingSectionMediaVideoPlaybackSyncSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makeCascadingPanelMediaVideoPlaybackSyncSelector(
      workspaceSettingsRef,
      sectionRef,
      panelRef
    ),
    (
      workspaceVideoPlaybackSync,
      sectionCascadingVideoPlaybackSync,
      panelCascadingVideoPlaybackSync
    ) => {
      switch (settingsLevel) {
        case SettingsLevel.PANEL:
          return panelCascadingVideoPlaybackSync;
        case SettingsLevel.SECTION:
          return sectionCascadingVideoPlaybackSync;
        default:
          return workspaceVideoPlaybackSync ?? VIDEO_PLAYBACK_SYNC_DEFAULT;
      }
    }
  );
};

/**
 * Whether the sync toggle is locked because a higher level enabled it:
 * section is inherited from an enabled workspace value, and panel from an
 * enabled workspace or section value.
 */
export const makeMediaVideoPlaybackSyncIsInheritedSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel
): Selector<RootState, boolean> => {
  return createSelector(
    makeWorkspaceMediaVideoPlaybackSyncSelector(workspaceSettingsRef),
    makeSectionMediaVideoPlaybackSyncSelector(sectionRef),
    (workspaceVideoPlaybackSync, sectionVideoPlaybackSync) => {
      const isEnabledAtWorkspace = workspaceVideoPlaybackSync === true;
      const isEnabledAtSection = sectionVideoPlaybackSync === true;
      switch (settingsLevel) {
        case SettingsLevel.PANEL:
          return isEnabledAtWorkspace || isEnabledAtSection;
        case SettingsLevel.SECTION:
          return isEnabledAtWorkspace;
        default:
          return false;
      }
    }
  );
};

/**
 * Component remount key for VideoPlaybackSync — mirrors getComponentKey on cascaded config.
 */
export const makeVideoPlaybackSyncComponentKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, string> => {
  return createSelector(
    makeMediaVideoPlaybackSyncDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    videoPlaybackSync => `${videoPlaybackSync.toString()}`
  );
};

const VIDEO_PLAYBACK_SYNC_AUTOPLAY_DEFAULT = false;

function getCascadingVideoPlaybackSyncAutoplay(
  workspaceVideoPlaybackSyncAutoplay: boolean | undefined,
  sectionVideoPlaybackSyncAutoplay: boolean | undefined
): boolean {
  return (
    sectionVideoPlaybackSyncAutoplay ??
    workspaceVideoPlaybackSyncAutoplay ??
    VIDEO_PLAYBACK_SYNC_AUTOPLAY_DEFAULT
  );
}

export const makeCascadingSectionMediaVideoPlaybackSyncAutoplaySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined
): Selector<RootState, boolean> => {
  return createSelector(
    makeWorkspaceSettingsVideoPlaybackSyncAutoplaySelector(
      workspaceSettingsRef
    ),
    makeSectionMediaVideoPlaybackSyncAutoplaySelector(sectionRef),
    (workspaceVideoPlaybackSyncAutoplay, sectionVideoPlaybackSyncAutoplay) =>
      getCascadingVideoPlaybackSyncAutoplay(
        workspaceVideoPlaybackSyncAutoplay,
        sectionVideoPlaybackSyncAutoplay
      )
  );
};

/** Raw panel-level videoPlaybackSyncAutoplay from the panel's own config (not cascaded). */
export const makePanelMediaVideoPlaybackSyncAutoplaySelector = (
  panelRef: PanelRef | undefined
): Selector<RootState, boolean | undefined> => {
  if (panelRef == null) {
    return () => undefined;
  }
  return createSelector(selectPanel(panelRef), panel =>
    panel?.viewType === panelViews.mediaBrowser
      ? (panel.config as MediaBrowserPanelConfig).videoPlaybackSyncAutoplay
      : undefined
  );
};

/** Workspace → section → panel cascaded value for panel-level display and behavior. */
export const makeCascadingPanelMediaVideoPlaybackSyncAutoplaySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  panelRef: PanelRef | undefined
): Selector<RootState, boolean> => {
  return createSelector(
    makeCascadingSectionMediaVideoPlaybackSyncAutoplaySelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makePanelMediaVideoPlaybackSyncAutoplaySelector(panelRef),
    (
      sectionCascadingVideoPlaybackSyncAutoplay,
      panelVideoPlaybackSyncAutoplay
    ) =>
      panelVideoPlaybackSyncAutoplay ??
      sectionCascadingVideoPlaybackSyncAutoplay
  );
};

export const makeMediaVideoPlaybackSyncAutoplayDisplaySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> => {
  return createSelector(
    makeWorkspaceSettingsVideoPlaybackSyncAutoplaySelector(
      workspaceSettingsRef
    ),
    makeCascadingSectionMediaVideoPlaybackSyncAutoplaySelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makeCascadingPanelMediaVideoPlaybackSyncAutoplaySelector(
      workspaceSettingsRef,
      sectionRef,
      panelRef
    ),
    (
      workspaceVideoPlaybackSyncAutoplay,
      sectionCascadingVideoPlaybackSyncAutoplay,
      panelCascadingVideoPlaybackSyncAutoplay
    ) => {
      switch (settingsLevel) {
        case SettingsLevel.PANEL:
          return panelCascadingVideoPlaybackSyncAutoplay;
        case SettingsLevel.SECTION:
          return sectionCascadingVideoPlaybackSyncAutoplay;
        default:
          return (
            workspaceVideoPlaybackSyncAutoplay ??
            VIDEO_PLAYBACK_SYNC_AUTOPLAY_DEFAULT
          );
      }
    }
  );
};

/**
 * Component remount key for VideoPlaybackSyncAutoplay — mirrors getComponentKey on cascaded config.
 */
export const makeVideoPlaybackSyncAutoplayComponentKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, string> => {
  return createSelector(
    makeMediaVideoPlaybackSyncAutoplayDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    videoPlaybackSyncAutoplay => `${videoPlaybackSyncAutoplay.toString()}`
  );
};

const VIDEO_PLAYBACK_SYNC_LOOP_DEFAULT = false;

function getCascadingVideoPlaybackSyncLoop(
  workspaceVideoPlaybackSyncLoop: boolean | undefined,
  sectionVideoPlaybackSyncLoop: boolean | undefined
): boolean {
  return (
    sectionVideoPlaybackSyncLoop ??
    workspaceVideoPlaybackSyncLoop ??
    VIDEO_PLAYBACK_SYNC_LOOP_DEFAULT
  );
}

export const makeCascadingSectionMediaVideoPlaybackSyncLoopSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined
): Selector<RootState, boolean> => {
  return createSelector(
    makeWorkspaceSettingsVideoPlaybackSyncLoopSelector(workspaceSettingsRef),
    makeSectionMediaVideoPlaybackSyncLoopSelector(sectionRef),
    (workspaceVideoPlaybackSyncLoop, sectionVideoPlaybackSyncLoop) =>
      getCascadingVideoPlaybackSyncLoop(
        workspaceVideoPlaybackSyncLoop,
        sectionVideoPlaybackSyncLoop
      )
  );
};

/** Raw panel-level videoPlaybackSyncLoop from the panel's own config (not cascaded). */
export const makePanelMediaVideoPlaybackSyncLoopSelector = (
  panelRef: PanelRef | undefined
): Selector<RootState, boolean | undefined> => {
  if (panelRef == null) {
    return () => undefined;
  }
  return createSelector(selectPanel(panelRef), panel =>
    panel?.viewType === panelViews.mediaBrowser
      ? (panel.config as MediaBrowserPanelConfig).videoPlaybackSyncLoop
      : undefined
  );
};

/** Workspace → section → panel cascaded value for panel-level display and behavior. */
export const makeCascadingPanelMediaVideoPlaybackSyncLoopSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  panelRef: PanelRef | undefined
): Selector<RootState, boolean> => {
  return createSelector(
    makeCascadingSectionMediaVideoPlaybackSyncLoopSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makePanelMediaVideoPlaybackSyncLoopSelector(panelRef),
    (sectionCascadingVideoPlaybackSyncLoop, panelVideoPlaybackSyncLoop) =>
      panelVideoPlaybackSyncLoop ?? sectionCascadingVideoPlaybackSyncLoop
  );
};

export const makeMediaVideoPlaybackSyncLoopDisplaySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> => {
  return createSelector(
    makeWorkspaceSettingsVideoPlaybackSyncLoopSelector(workspaceSettingsRef),
    makeCascadingSectionMediaVideoPlaybackSyncLoopSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makeCascadingPanelMediaVideoPlaybackSyncLoopSelector(
      workspaceSettingsRef,
      sectionRef,
      panelRef
    ),
    (
      workspaceVideoPlaybackSyncLoop,
      sectionCascadingVideoPlaybackSyncLoop,
      panelCascadingVideoPlaybackSyncLoop
    ) => {
      switch (settingsLevel) {
        case SettingsLevel.PANEL:
          return panelCascadingVideoPlaybackSyncLoop;
        case SettingsLevel.SECTION:
          return sectionCascadingVideoPlaybackSyncLoop;
        default:
          return (
            workspaceVideoPlaybackSyncLoop ?? VIDEO_PLAYBACK_SYNC_LOOP_DEFAULT
          );
      }
    }
  );
};

/**
 * Component remount key for VideoPlaybackSyncLoop — mirrors getComponentKey on cascaded config.
 */
export const makeVideoPlaybackSyncLoopComponentKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, string> => {
  return createSelector(
    makeMediaVideoPlaybackSyncLoopDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    videoPlaybackSyncLoop => `${videoPlaybackSyncLoop.toString()}`
  );
};

export const makeCascadingSectionMediaVideoPlayerTimeStepSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined
): Selector<RootState, number | undefined> => {
  return createSelector(
    makeWorkspaceMediaVideoPlayerTimeStepSelector(workspaceSettingsRef),
    makeSectionMediaVideoPlayerTimeStepSelector(sectionRef),
    (workspaceVideoPlayerTimeStep, sectionVideoPlayerTimeStep) =>
      sectionVideoPlayerTimeStep ?? workspaceVideoPlayerTimeStep
  );
};

/** Raw panel-level videoPlayerTimeStep from the panel's own config (not cascaded). */
export const makePanelMediaVideoPlayerTimeStepSelector = (
  panelRef: PanelRef | undefined
): Selector<RootState, number | undefined> => {
  if (panelRef == null) {
    return () => undefined;
  }
  return createSelector(selectPanel(panelRef), panel =>
    panel?.viewType === panelViews.mediaBrowser
      ? (panel.config as MediaBrowserPanelConfig).videoPlayerTimeStep
      : undefined
  );
};

/** Workspace → section → panel cascaded value for panel-level display and behavior. */
export const makeCascadingPanelMediaVideoPlayerTimeStepSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  panelRef: PanelRef | undefined
): Selector<RootState, number | undefined> => {
  return createSelector(
    makeCascadingSectionMediaVideoPlayerTimeStepSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makePanelMediaVideoPlayerTimeStepSelector(panelRef),
    (sectionCascadingVideoPlayerTimeStep, panelVideoPlayerTimeStep) =>
      panelVideoPlayerTimeStep ?? sectionCascadingVideoPlayerTimeStep
  );
};

export const makeMediaVideoPlayerTimeStepDisplaySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, number | undefined> => {
  return createSelector(
    makeWorkspaceMediaVideoPlayerTimeStepSelector(workspaceSettingsRef),
    makeCascadingSectionMediaVideoPlayerTimeStepSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makeCascadingPanelMediaVideoPlayerTimeStepSelector(
      workspaceSettingsRef,
      sectionRef,
      panelRef
    ),
    (
      workspaceVideoPlayerTimeStep,
      sectionCascadingVideoPlayerTimeStep,
      panelCascadingVideoPlayerTimeStep
    ) => {
      switch (settingsLevel) {
        case SettingsLevel.PANEL:
          return panelCascadingVideoPlayerTimeStep;
        case SettingsLevel.SECTION:
          return sectionCascadingVideoPlayerTimeStep;
        default:
          return workspaceVideoPlayerTimeStep;
      }
    }
  );
};

/**
 * Component remount key for VideoPlayerTimeStep — mirrors getComponentKey on cascaded config.
 */
export const makeVideoPlayerTimeStepComponentKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, string> => {
  return createSelector(
    makeMediaVideoPlayerTimeStepDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    videoPlayerTimeStep => `${videoPlayerTimeStep?.toString()}`
  );
};

type CompareSettings = MediaBrowserPanelConfig['compareSettings'];

/** No default: undefined means the drawer's default compare behavior. */
export const makeCascadingSectionMediaCompareSettingsSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined
): Selector<RootState, CompareSettings> => {
  return createSelector(
    makeWorkspaceMediaCompareSettingsSelector(workspaceSettingsRef),
    makeSectionMediaCompareSettingsSelector(sectionRef),
    (workspaceCompareSettings, sectionCompareSettings) =>
      sectionCompareSettings ?? workspaceCompareSettings
  );
};

/** Raw panel-level compareSettings from the panel's own config (not cascaded). */
export const makePanelMediaCompareSettingsSelector = (
  panelRef: PanelRef | undefined
): Selector<RootState, CompareSettings> => {
  if (panelRef == null) {
    return () => undefined;
  }
  return createSelector(selectPanel(panelRef), panel =>
    panel?.viewType === panelViews.mediaBrowser
      ? (panel.config as MediaBrowserPanelConfig).compareSettings
      : undefined
  );
};

/** Workspace → section → panel cascaded value for panel-level display and behavior. */
export const makeCascadingPanelMediaCompareSettingsSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  panelRef: PanelRef | undefined
): Selector<RootState, CompareSettings> => {
  return createSelector(
    makeCascadingSectionMediaCompareSettingsSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makePanelMediaCompareSettingsSelector(panelRef),
    (sectionCascadingCompareSettings, panelCompareSettings) =>
      panelCompareSettings ?? sectionCascadingCompareSettings
  );
};

export const makeMediaCompareSettingsDisplaySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, CompareSettings> => {
  return createSelector(
    makeWorkspaceMediaCompareSettingsSelector(workspaceSettingsRef),
    makeCascadingSectionMediaCompareSettingsSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makeCascadingPanelMediaCompareSettingsSelector(
      workspaceSettingsRef,
      sectionRef,
      panelRef
    ),
    (
      workspaceCompareSettings,
      sectionCascadingCompareSettings,
      panelCascadingCompareSettings
    ) => {
      switch (settingsLevel) {
        case SettingsLevel.PANEL:
          return panelCascadingCompareSettings;
        case SettingsLevel.SECTION:
          return sectionCascadingCompareSettings;
        default:
          return workspaceCompareSettings;
      }
    }
  );
};

type BoundingBoxConfig = MediaBrowserPanelConfig['boundingBoxConfig'];

/** No default: undefined means the default bounding box display behavior. */
export const makeCascadingSectionMediaBoundingBoxConfigSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined
): Selector<RootState, BoundingBoxConfig> => {
  return createSelector(
    makeWorkspaceMediaBoundingBoxConfigSelector(workspaceSettingsRef),
    makeSectionMediaBoundingBoxConfigSelector(sectionRef),
    (workspaceBoundingBoxConfig, sectionBoundingBoxConfig) =>
      sectionBoundingBoxConfig ?? workspaceBoundingBoxConfig
  );
};

/** Raw panel-level boundingBoxConfig from the panel's own config (not cascaded). */
export const makePanelMediaBoundingBoxConfigSelector = (
  panelRef: PanelRef | undefined
): Selector<RootState, BoundingBoxConfig> => {
  if (panelRef == null) {
    return () => undefined;
  }
  return createSelector(selectPanel(panelRef), panel =>
    panel?.viewType === panelViews.mediaBrowser
      ? (panel.config as MediaBrowserPanelConfig).boundingBoxConfig
      : undefined
  );
};

/** Workspace → section → panel cascaded value for panel-level display and behavior. */
export const makeCascadingPanelMediaBoundingBoxConfigSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  panelRef: PanelRef | undefined
): Selector<RootState, BoundingBoxConfig> => {
  return createSelector(
    makeCascadingSectionMediaBoundingBoxConfigSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makePanelMediaBoundingBoxConfigSelector(panelRef),
    (sectionCascadingBoundingBoxConfig, panelBoundingBoxConfig) =>
      panelBoundingBoxConfig ?? sectionCascadingBoundingBoxConfig
  );
};

export const makeMediaBoundingBoxConfigDisplaySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, BoundingBoxConfig> => {
  return createSelector(
    makeWorkspaceMediaBoundingBoxConfigSelector(workspaceSettingsRef),
    makeCascadingSectionMediaBoundingBoxConfigSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makeCascadingPanelMediaBoundingBoxConfigSelector(
      workspaceSettingsRef,
      sectionRef,
      panelRef
    ),
    (
      workspaceBoundingBoxConfig,
      sectionCascadingBoundingBoxConfig,
      panelCascadingBoundingBoxConfig
    ) => {
      switch (settingsLevel) {
        case SettingsLevel.PANEL:
          return panelCascadingBoundingBoxConfig;
        case SettingsLevel.SECTION:
          return sectionCascadingBoundingBoxConfig;
        default:
          return workspaceBoundingBoxConfig;
      }
    }
  );
};

type SegmentationMaskConfig = MediaBrowserPanelConfig['segmentationMaskConfig'];

/** No default: undefined means the default segmentation mask display behavior. */
export const makeCascadingSectionMediaSegmentationMaskConfigSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined
): Selector<RootState, SegmentationMaskConfig> => {
  return createSelector(
    makeWorkspaceMediaSegmentationMaskConfigSelector(workspaceSettingsRef),
    makeSectionMediaSegmentationMaskConfigSelector(sectionRef),
    (workspaceSegmentationMaskConfig, sectionSegmentationMaskConfig) =>
      sectionSegmentationMaskConfig ?? workspaceSegmentationMaskConfig
  );
};

/** Raw panel-level segmentationMaskConfig from the panel's own config (not cascaded). */
export const makePanelMediaSegmentationMaskConfigSelector = (
  panelRef: PanelRef | undefined
): Selector<RootState, SegmentationMaskConfig> => {
  if (panelRef == null) {
    return () => undefined;
  }
  return createSelector(selectPanel(panelRef), panel =>
    panel?.viewType === panelViews.mediaBrowser
      ? (panel.config as MediaBrowserPanelConfig).segmentationMaskConfig
      : undefined
  );
};

/** Workspace → section → panel cascaded value for panel-level display and behavior. */
export const makeCascadingPanelMediaSegmentationMaskConfigSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  panelRef: PanelRef | undefined
): Selector<RootState, SegmentationMaskConfig> => {
  return createSelector(
    makeCascadingSectionMediaSegmentationMaskConfigSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makePanelMediaSegmentationMaskConfigSelector(panelRef),
    (sectionCascadingSegmentationMaskConfig, panelSegmentationMaskConfig) =>
      panelSegmentationMaskConfig ?? sectionCascadingSegmentationMaskConfig
  );
};

export const makeMediaSegmentationMaskConfigDisplaySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, SegmentationMaskConfig> => {
  return createSelector(
    makeWorkspaceMediaSegmentationMaskConfigSelector(workspaceSettingsRef),
    makeCascadingSectionMediaSegmentationMaskConfigSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makeCascadingPanelMediaSegmentationMaskConfigSelector(
      workspaceSettingsRef,
      sectionRef,
      panelRef
    ),
    (
      workspaceSegmentationMaskConfig,
      sectionCascadingSegmentationMaskConfig,
      panelCascadingSegmentationMaskConfig
    ) => {
      switch (settingsLevel) {
        case SettingsLevel.PANEL:
          return panelCascadingSegmentationMaskConfig;
        case SettingsLevel.SECTION:
          return sectionCascadingSegmentationMaskConfig;
        default:
          return workspaceSegmentationMaskConfig;
      }
    }
  );
};

/**
 * Raw panel-level chartTitle from the panel's own config.
 * chartTitle is a panel-only setting: it never cascades from workspace or section.
 */
export const makePanelMediaChartTitleSelector = (
  panelRef: PanelRef | undefined
): Selector<RootState, MediaBrowserPanelConfig['chartTitle']> => {
  if (panelRef == null) {
    return () => undefined;
  }
  return createSelector(selectPanel(panelRef), panel =>
    panel?.viewType === panelViews.mediaBrowser
      ? (panel.config as MediaBrowserPanelConfig).chartTitle
      : undefined
  );
};

/**
 * Raw panel-level mediaKeys from the panel's own config.
 * mediaKeys is a panel-only setting: it never cascades from workspace or section.
 */
export const makePanelMediaKeysSelector = (
  panelRef: PanelRef | undefined
): Selector<RootState, MediaBrowserPanelConfig['mediaKeys']> => {
  if (panelRef == null) {
    return () => undefined;
  }
  return createSelector(selectPanel(panelRef), panel =>
    panel?.viewType === panelViews.mediaBrowser
      ? (panel.config as MediaBrowserPanelConfig).mediaKeys
      : undefined
  );
};

const USE_RIGHT_HANDED_SYSTEM_DEFAULT = false;

export const makeCascadingSectionMediaUseRightHandedSystemSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined
): Selector<RootState, boolean> => {
  return createSelector(
    makeWorkspaceMediaUseRightHandedSystemSelector(workspaceSettingsRef),
    makeSectionMediaUseRightHandedSystemSelector(sectionRef),
    (workspaceUseRightHandedSystem, sectionUseRightHandedSystem) =>
      (sectionUseRightHandedSystem ??
        workspaceUseRightHandedSystem ??
        USE_RIGHT_HANDED_SYSTEM_DEFAULT) === true
  );
};

/** Raw panel-level useRightHandedSystem from the panel's own config (not cascaded). */
export const makePanelMediaUseRightHandedSystemSelector = (
  panelRef: PanelRef | undefined
): Selector<RootState, boolean | undefined> => {
  if (panelRef == null) {
    return () => undefined;
  }
  return createSelector(selectPanel(panelRef), panel =>
    panel?.viewType === panelViews.mediaBrowser
      ? (panel.config as MediaBrowserPanelConfig).useRightHandedSystem
      : undefined
  );
};

/** Workspace → section → panel cascaded value for panel-level display and behavior. */
export const makeCascadingPanelMediaUseRightHandedSystemSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  panelRef: PanelRef | undefined
): Selector<RootState, boolean> => {
  return createSelector(
    makeCascadingSectionMediaUseRightHandedSystemSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makePanelMediaUseRightHandedSystemSelector(panelRef),
    (sectionCascadingUseRightHandedSystem, panelUseRightHandedSystem) =>
      panelUseRightHandedSystem ?? sectionCascadingUseRightHandedSystem
  );
};

export const makeMediaUseRightHandedSystemDisplaySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> => {
  return createSelector(
    makeWorkspaceMediaUseRightHandedSystemSelector(workspaceSettingsRef),
    makeCascadingSectionMediaUseRightHandedSystemSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makeCascadingPanelMediaUseRightHandedSystemSelector(
      workspaceSettingsRef,
      sectionRef,
      panelRef
    ),
    (
      workspaceUseRightHandedSystem,
      sectionCascadingUseRightHandedSystem,
      panelCascadingUseRightHandedSystem
    ) => {
      switch (settingsLevel) {
        case SettingsLevel.PANEL:
          return panelCascadingUseRightHandedSystem;
        case SettingsLevel.SECTION:
          return sectionCascadingUseRightHandedSystem;
        default:
          return (
            workspaceUseRightHandedSystem ?? USE_RIGHT_HANDED_SYSTEM_DEFAULT
          );
      }
    }
  );
};

/**
 * Component remount key for AxesHandedness — mirrors getComponentKey on cascaded config.
 */
export const makeUseRightHandedSystemComponentKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, string> => {
  return createSelector(
    makeMediaUseRightHandedSystemDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    useRightHandedSystem => `${useRightHandedSystem.toString()}`
  );
};

type PointColor = MediaBrowserPanelConfig['pointColor'];

/** No default: undefined means "use the default logged color". */
export const makeCascadingSectionMediaPointColorSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined
): Selector<RootState, PointColor> => {
  return createSelector(
    makeWorkspaceMediaPointColorSelector(workspaceSettingsRef),
    makeSectionMediaPointColorSelector(sectionRef),
    (workspacePointColor, sectionPointColor) =>
      sectionPointColor ?? workspacePointColor
  );
};

/** Raw panel-level pointColor from the panel's own config (not cascaded). */
export const makePanelMediaPointColorSelector = (
  panelRef: PanelRef | undefined
): Selector<RootState, PointColor> => {
  if (panelRef == null) {
    return () => undefined;
  }
  return createSelector(selectPanel(panelRef), panel =>
    panel?.viewType === panelViews.mediaBrowser
      ? (panel.config as MediaBrowserPanelConfig).pointColor
      : undefined
  );
};

/** Workspace → section → panel cascaded value for panel-level display and behavior. */
export const makeCascadingPanelMediaPointColorSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  panelRef: PanelRef | undefined
): Selector<RootState, PointColor> => {
  return createSelector(
    makeCascadingSectionMediaPointColorSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makePanelMediaPointColorSelector(panelRef),
    (sectionCascadingPointColor, panelPointColor) =>
      panelPointColor ?? sectionCascadingPointColor
  );
};

export const makeMediaPointColorDisplaySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, PointColor> => {
  return createSelector(
    makeWorkspaceMediaPointColorSelector(workspaceSettingsRef),
    makeCascadingSectionMediaPointColorSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makeCascadingPanelMediaPointColorSelector(
      workspaceSettingsRef,
      sectionRef,
      panelRef
    ),
    (
      workspacePointColor,
      sectionCascadingPointColor,
      panelCascadingPointColor
    ) => {
      switch (settingsLevel) {
        case SettingsLevel.PANEL:
          return panelCascadingPointColor;
        case SettingsLevel.SECTION:
          return sectionCascadingPointColor;
        default:
          return workspacePointColor;
      }
    }
  );
};

type Object3DBackgroundColor = NonNullable<
  MediaBrowserPanelConfig['object3DBackgroundColor']
>;

// Default background color for Babylon Scene is 0.2, 0.2, 0.3, 1 on a scale of 0-1. This converts it to 0-255.
export const OBJECT3D_BACKGROUND_COLOR_DEFAULT: Object3DBackgroundColor = {
  r: Math.floor(0.2 * 255),
  g: Math.floor(0.2 * 255),
  b: Math.floor(0.3 * 255),
  a: 1,
};

function getCascadingObject3DBackgroundColor(
  workspaceObject3DBackgroundColor: Object3DBackgroundColor | undefined,
  sectionObject3DBackgroundColor: Object3DBackgroundColor | undefined
): Object3DBackgroundColor {
  return (
    sectionObject3DBackgroundColor ??
    workspaceObject3DBackgroundColor ??
    OBJECT3D_BACKGROUND_COLOR_DEFAULT
  );
}

export const makeCascadingSectionMediaObject3DBackgroundColorSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined
): Selector<RootState, Object3DBackgroundColor> => {
  return createSelector(
    makeWorkspaceMediaObject3DBackgroundColorSelector(workspaceSettingsRef),
    makeSectionMediaObject3DBackgroundColorSelector(sectionRef),
    (workspaceObject3DBackgroundColor, sectionObject3DBackgroundColor) =>
      getCascadingObject3DBackgroundColor(
        workspaceObject3DBackgroundColor,
        sectionObject3DBackgroundColor
      )
  );
};

/** Raw panel-level object3DBackgroundColor from the panel's own config (not cascaded). */
export const makePanelMediaObject3DBackgroundColorSelector = (
  panelRef: PanelRef | undefined
): Selector<RootState, Object3DBackgroundColor | undefined> => {
  if (panelRef == null) {
    return () => undefined;
  }
  return createSelector(selectPanel(panelRef), panel =>
    panel?.viewType === panelViews.mediaBrowser
      ? (panel.config as MediaBrowserPanelConfig).object3DBackgroundColor
      : undefined
  );
};

/** Workspace → section → panel cascaded value for panel-level display and behavior. */
export const makeCascadingPanelMediaObject3DBackgroundColorSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  panelRef: PanelRef | undefined
): Selector<RootState, Object3DBackgroundColor> => {
  return createSelector(
    makeCascadingSectionMediaObject3DBackgroundColorSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makePanelMediaObject3DBackgroundColorSelector(panelRef),
    (sectionCascadingObject3DBackgroundColor, panelObject3DBackgroundColor) =>
      panelObject3DBackgroundColor ?? sectionCascadingObject3DBackgroundColor
  );
};

export const makeMediaObject3DBackgroundColorDisplaySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, Object3DBackgroundColor> => {
  return createSelector(
    makeWorkspaceMediaObject3DBackgroundColorSelector(workspaceSettingsRef),
    makeCascadingSectionMediaObject3DBackgroundColorSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makeCascadingPanelMediaObject3DBackgroundColorSelector(
      workspaceSettingsRef,
      sectionRef,
      panelRef
    ),
    (
      workspaceObject3DBackgroundColor,
      sectionCascadingObject3DBackgroundColor,
      panelCascadingObject3DBackgroundColor
    ) => {
      switch (settingsLevel) {
        case SettingsLevel.PANEL:
          return panelCascadingObject3DBackgroundColor;
        case SettingsLevel.SECTION:
          return sectionCascadingObject3DBackgroundColor;
        default:
          return (
            workspaceObject3DBackgroundColor ??
            OBJECT3D_BACKGROUND_COLOR_DEFAULT
          );
      }
    }
  );
};

const DISABLE_VIDEO_STREAM_DEFAULT = false;

function getCascadingDisableVideoStream(
  workspaceDisableVideoStream: boolean | undefined,
  sectionDisableVideoStream: boolean | undefined
): boolean {
  return (
    sectionDisableVideoStream ??
    workspaceDisableVideoStream ??
    DISABLE_VIDEO_STREAM_DEFAULT
  );
}

export const makeCascadingSectionMediaDisableVideoStreamSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined
): Selector<RootState, boolean> => {
  return createSelector(
    makeWorkspaceMediaDisableVideoStreamSelector(workspaceSettingsRef),
    makeSectionMediaDisableVideoStreamSelector(sectionRef),
    (workspaceDisableVideoStream, sectionDisableVideoStream) =>
      getCascadingDisableVideoStream(
        workspaceDisableVideoStream,
        sectionDisableVideoStream
      )
  );
};

/** Raw panel-level disableVideoStream from the panel's own config (not cascaded). */
export const makePanelMediaDisableVideoStreamSelector = (
  panelRef: PanelRef | undefined
): Selector<RootState, boolean | undefined> => {
  if (panelRef == null) {
    return () => undefined;
  }
  return createSelector(selectPanel(panelRef), panel =>
    panel?.viewType === panelViews.mediaBrowser
      ? (panel.config as MediaBrowserPanelConfig).disableVideoStream
      : undefined
  );
};

/** Workspace → section → panel cascaded value for panel-level display and behavior. */
export const makeCascadingPanelMediaDisableVideoStreamSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  panelRef: PanelRef | undefined
): Selector<RootState, boolean> => {
  return createSelector(
    makeCascadingSectionMediaDisableVideoStreamSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makePanelMediaDisableVideoStreamSelector(panelRef),
    (sectionCascadingDisableVideoStream, panelDisableVideoStream) =>
      panelDisableVideoStream ?? sectionCascadingDisableVideoStream
  );
};

export const makeMediaDisableVideoStreamDisplaySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> => {
  return createSelector(
    makeWorkspaceMediaDisableVideoStreamSelector(workspaceSettingsRef),
    makeCascadingSectionMediaDisableVideoStreamSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makeCascadingPanelMediaDisableVideoStreamSelector(
      workspaceSettingsRef,
      sectionRef,
      panelRef
    ),
    (
      workspaceDisableVideoStream,
      sectionCascadingDisableVideoStream,
      panelCascadingDisableVideoStream
    ) => {
      switch (settingsLevel) {
        case SettingsLevel.PANEL:
          return panelCascadingDisableVideoStream;
        case SettingsLevel.SECTION:
          return sectionCascadingDisableVideoStream;
        default:
          return workspaceDisableVideoStream ?? DISABLE_VIDEO_STREAM_DEFAULT;
      }
    }
  );
};

const PIXELATED_DEFAULT = true;

function getCascadingPixelated(
  workspacePixelated: boolean | undefined,
  sectionPixelated: boolean | undefined
): boolean {
  return sectionPixelated ?? workspacePixelated ?? PIXELATED_DEFAULT;
}

export const makeCascadingSectionMediaPixelatedSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined
): Selector<RootState, boolean> => {
  return createSelector(
    makeWorkspaceMediaPixelatedSelector(workspaceSettingsRef),
    makeSectionMediaPixelatedSelector(sectionRef),
    (workspacePixelated, sectionPixelated) =>
      getCascadingPixelated(workspacePixelated, sectionPixelated)
  );
};

/** Raw panel-level pixelated from the panel's own config (not cascaded). */
export const makePanelMediaPixelatedSelector = (
  panelRef: PanelRef | undefined
): Selector<RootState, boolean | undefined> => {
  if (panelRef == null) {
    return () => undefined;
  }
  return createSelector(selectPanel(panelRef), panel =>
    panel?.viewType === panelViews.mediaBrowser
      ? (panel.config as MediaBrowserPanelConfig).pixelated
      : undefined
  );
};

/** Workspace → section → panel cascaded value for panel-level display and behavior. */
export const makeCascadingPanelMediaPixelatedSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  panelRef: PanelRef | undefined
): Selector<RootState, boolean> => {
  return createSelector(
    makeCascadingSectionMediaPixelatedSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makePanelMediaPixelatedSelector(panelRef),
    (sectionCascadingPixelated, panelPixelated) =>
      panelPixelated ?? sectionCascadingPixelated
  );
};

export const makeMediaPixelatedDisplaySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> => {
  return createSelector(
    makeWorkspaceMediaPixelatedSelector(workspaceSettingsRef),
    makeCascadingSectionMediaPixelatedSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makeCascadingPanelMediaPixelatedSelector(
      workspaceSettingsRef,
      sectionRef,
      panelRef
    ),
    (
      workspacePixelated,
      sectionCascadingPixelated,
      panelCascadingPixelated
    ) => {
      switch (settingsLevel) {
        case SettingsLevel.PANEL:
          return panelCascadingPixelated;
        case SettingsLevel.SECTION:
          return sectionCascadingPixelated;
        default:
          return workspacePixelated ?? PIXELATED_DEFAULT;
      }
    }
  );
};

/**
 * Whether to show override clear rows for SmoothImage — mirrors
 * `config.pixelated != null` on config with defaults, which always
 * resolves to a value.
 */
export const makePixelatedShowOverridesSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> => {
  return createSelector(
    makeMediaPixelatedDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    pixelated => pixelated != null
  );
};

/**
 * Component remount key for SmoothImage — mirrors getComponentKey on cascaded config.
 */
export const makePixelatedComponentKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, string> => {
  return createSelector(
    makeMediaPixelatedDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    pixelated => `${pixelated.toString()}`
  );
};

export const makeCascadingSectionMediaStepStrideLengthSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined
): Selector<RootState, number | undefined> => {
  return createSelector(
    makeWorkspaceMediaStepStrideLengthSelector(workspaceSettingsRef),
    makeSectionMediaStepStrideLengthSelector(sectionRef),
    (workspaceStepStrideLength, sectionStepStrideLength) =>
      sectionStepStrideLength ?? workspaceStepStrideLength
  );
};

/** Raw panel-level stepStrideLength from the panel's own config (not cascaded). */
export const makePanelMediaStepStrideLengthSelector = (
  panelRef: PanelRef | undefined
): Selector<RootState, number | undefined> => {
  if (panelRef == null) {
    return () => undefined;
  }
  return createSelector(selectPanel(panelRef), panel =>
    panel?.viewType === panelViews.mediaBrowser
      ? (panel.config as MediaBrowserPanelConfig).stepStrideLength
      : undefined
  );
};

/** Workspace → section → panel cascaded value for panel-level display and behavior. */
export const makeCascadingPanelMediaStepStrideLengthSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  panelRef: PanelRef | undefined
): Selector<RootState, number | undefined> => {
  return createSelector(
    makeCascadingSectionMediaStepStrideLengthSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makePanelMediaStepStrideLengthSelector(panelRef),
    (sectionCascadingStepStrideLength, panelStepStrideLength) =>
      panelStepStrideLength ?? sectionCascadingStepStrideLength
  );
};

export const makeMediaStepStrideLengthDisplaySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, number | undefined> => {
  return createSelector(
    makeWorkspaceMediaStepStrideLengthSelector(workspaceSettingsRef),
    makeCascadingSectionMediaStepStrideLengthSelector(
      workspaceSettingsRef,
      sectionRef
    ),
    makeCascadingPanelMediaStepStrideLengthSelector(
      workspaceSettingsRef,
      sectionRef,
      panelRef
    ),
    (
      workspaceStepStrideLength,
      sectionCascadingStepStrideLength,
      panelCascadingStepStrideLength
    ) => {
      switch (settingsLevel) {
        case SettingsLevel.PANEL:
          return panelCascadingStepStrideLength;
        case SettingsLevel.SECTION:
          return sectionCascadingStepStrideLength;
        default:
          return workspaceStepStrideLength;
      }
    }
  );
};

/**
 * Whether to show override clear rows for StrideLength — mirrors
 * `config.stepStrideLength != null` on cascaded config.
 */
export const makeStepStrideLengthShowOverridesSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> => {
  return createSelector(
    makeMediaStepStrideLengthDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    stepStrideLength => stepStrideLength != null
  );
};

/**
 * Component remount key for StrideLength — mirrors getComponentKey on cascaded config.
 */
export const makeStepStrideLengthComponentKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, string> => {
  return createSelector(
    makeMediaStepStrideLengthDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    stepStrideLength => `${stepStrideLength?.toString()}`
  );
};

/**
 * Whether to show override clear rows for SliderSync — mirrors
 * `config.sliderSync != null` on spread-cascaded media config.
 */
export const makeSliderSyncShowOverridesSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel
): Selector<RootState, boolean> => {
  return createSelector(
    makeWorkspaceMediaSliderSyncSelector(workspaceSettingsRef),
    makeSectionMediaSliderSyncSelector(sectionRef),
    (workspaceSliderSync, sectionSliderSync) => {
      const sliderSync =
        settingsLevel === SettingsLevel.SECTION
          ? sectionSliderSync !== undefined
            ? sectionSliderSync
            : workspaceSliderSync
          : workspaceSliderSync;
      return sliderSync != null;
    }
  );
};

/**
 * Component remount key for SliderSync — mirrors getComponentKey on cascaded config.
 */
export const makeSliderSyncComponentKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel
): Selector<RootState, string> => {
  return createSelector(
    makeMediaSliderSyncDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel
    ),
    makeMediaSliderKeyDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel
    ),
    (sliderSync, mediaSliderKey) =>
      `${sliderSync.toString()},${mediaSliderKey.toString()}`
  );
};

// =============================================================================
// Per-setting shouldShow selectors (drawer row visibility)
// =============================================================================

export const makeModeIsCompareShouldShowSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> =>
  createSelector(
    makeMediaModeDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    mode => mode === 'compare'
  );

export const makeModeIsNotCompareShouldShowSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> =>
  createSelector(
    makeMediaModeDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    mode => mode !== 'compare'
  );

export const makeModeIsGalleryShouldShowSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> =>
  createSelector(
    makeMediaModeDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    mode => mode === 'gallery'
  );

export const makeModeIsGridShouldShowSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> =>
  createSelector(
    makeMediaModeDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    mode => mode === 'grid'
  );

/**
 * Sliders like columnCount/maxGalleryItems/maxYAxisCount are hidden when the
 * media is sized by actualSize/fitToDimension. Those two settings only apply
 * in gallery mode (see `getConfigWithDefaults`), so they're treated as false
 * in other modes.
 */
const makeSizedByImageSettingsSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> =>
  createSelector(
    makeMediaModeDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    makeMediaActualSizeDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    makeMediaFitToDimensionDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    (mode, actualSize, fitToDimension) =>
      mode === 'gallery' && (actualSize || fitToDimension)
  );

export const makeColumnCountShouldShowSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> =>
  createSelector(
    makeSizedByImageSettingsSelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    sizedByImageSettings =>
      // There's no point in showing the slider if there's not more than one column number option
      MAX_COL_COUNT > MIN_COL_COUNT && !sizedByImageSettings
  );

export const makeMaxGalleryItemsShouldShowSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> =>
  createSelector(
    makeSizedByImageSettingsSelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    makeMediaModeDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    (sizedByImageSettings, mode) =>
      MAX_COL_COUNT > MIN_COL_COUNT &&
      !sizedByImageSettings &&
      mode === 'gallery'
  );

export const makeMaxYAxisCountShouldShowSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> =>
  createSelector(
    makeSizedByImageSettingsSelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    makeMediaModeDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    (sizedByImageSettings, mode) =>
      MAX_COL_COUNT > MIN_COL_COUNT && !sizedByImageSettings && mode === 'grid'
  );

const ALWAYS_VISIBLE_SELECTOR: Selector<RootState, boolean> = () => true;

/**
 * Whether any of the given settings' rows are visible. Settings without a
 * `shouldShowSelector` are always visible (static conditions are applied
 * beforehand in `getMediaSettingsTabs`).
 */
export const makeAnyMediaSettingVisibleSelector = (
  settings: MediaSetting[],
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> => {
  if (settings.length === 0) {
    return () => false;
  }
  const rowSelectors = settings.map(
    setting =>
      setting.shouldShowSelector?.(
        workspaceSettingsRef,
        sectionRef,
        settingsLevel,
        panelRef
      ) ?? ALWAYS_VISIBLE_SELECTOR
  );
  return createSelector(rowSelectors, (...rowsVisible: boolean[]) =>
    rowsVisible.some(Boolean)
  );
};

/** Titles of the tabs that have at least one visible setting row. */
export const makeVisibleMediaTabTitlesSelector = (
  tabs: MediaSettingsTab[],
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, string[]> => {
  if (tabs.length === 0) {
    return () => [];
  }
  const tabSelectors = tabs.map(tab =>
    makeAnyMediaSettingVisibleSelector(
      tab.sections.flatMap(section => section.settings),
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    )
  );
  return createSelector(tabSelectors, (...tabsVisible: boolean[]) =>
    tabs.filter((_, i) => tabsVisible[i]).map(tab => tab.title)
  );
};

// =============================================================================
// Cascaded panel media config (panel render path)
// =============================================================================

/** Raw panel config for a Media Browser panel (undefined for other viewTypes). */
export const makePanelMediaBrowserConfigSelector = (
  panelRef: PanelRef | undefined
): Selector<RootState, MediaBrowserPanelConfig | undefined> => {
  if (panelRef == null) {
    return () => undefined;
  }
  return createSelector(selectPanel(panelRef), panel =>
    panel?.viewType === panelViews.mediaBrowser
      ? (panel.config as MediaBrowserPanelConfig)
      : undefined
  );
};

const makeCascadingRawValueSelector = <T>(
  workspaceSelector: Selector<RootState, T | undefined>,
  sectionSelector: Selector<RootState, T | undefined>,
  panelSelector: Selector<RootState, T | undefined>
): Selector<RootState, T | undefined> =>
  createSelector(
    workspaceSelector,
    sectionSelector,
    panelSelector,
    (workspaceValue, sectionValue, panelValue) =>
      panelValue ?? sectionValue ?? workspaceValue
  );

/**
 * The panel's fully cascaded (workspace → section → panel) raw media config,
 * composed from the per-field selectors. This replaces injecting merged
 * settings into the panel via `inheritedSettings.media`; defaults are still
 * applied downstream by `getConfigWithDefaults`.
 */
export const makeCascadingPanelMediaConfigSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  panelRef: PanelRef | undefined
): Selector<RootState, MediaBrowserPanelConfig> => {
  const cascadedFieldsSelector = createStructuredSelector<
    RootState,
    Partial<MediaBrowserPanelConfig>
  >({
    mode: makeCascadingRawValueSelector(
      makeWorkspaceMediaModeSelector(workspaceSettingsRef),
      makeSectionMediaModeSelector(sectionRef),
      makePanelMediaModeSelector(panelRef)
    ),
    mediaSliderKey: makeCascadingRawValueSelector(
      makeWorkspaceMediaSliderKeySelector(workspaceSettingsRef),
      makeSectionMediaSliderKeySelector(sectionRef),
      makePanelMediaSliderKeySelector(panelRef)
    ),
    snapToExistingStep: makeCascadingRawValueSelector(
      makeWorkspaceMediaSnapToExistingStepSelector(workspaceSettingsRef),
      makeSectionMediaSnapToExistingStepSelector(sectionRef),
      makePanelMediaSnapToExistingStepSelector(panelRef)
    ),
    stepStrideLength: makeCascadingRawValueSelector(
      makeWorkspaceMediaStepStrideLengthSelector(workspaceSettingsRef),
      makeSectionMediaStepStrideLengthSelector(sectionRef),
      makePanelMediaStepStrideLengthSelector(panelRef)
    ),
    pixelated: makeCascadingRawValueSelector(
      makeWorkspaceMediaPixelatedSelector(workspaceSettingsRef),
      makeSectionMediaPixelatedSelector(sectionRef),
      makePanelMediaPixelatedSelector(panelRef)
    ),
    columnCount: makeCascadingRawValueSelector(
      makeWorkspaceMediaColumnCountSelector(workspaceSettingsRef),
      makeSectionMediaColumnCountSelector(sectionRef),
      makePanelMediaColumnCountSelector(panelRef)
    ),
    maxRuns: makeCascadingRawValueSelector(
      makeWorkspaceMediaMaxRunsSelector(workspaceSettingsRef),
      makeSectionMediaMaxRunsSelector(sectionRef),
      makePanelMediaMaxRunsSelector(panelRef)
    ),
    maxGalleryItems: makeCascadingRawValueSelector(
      makeWorkspaceMediaMaxGalleryItemsSelector(workspaceSettingsRef),
      makeSectionMediaMaxGalleryItemsSelector(sectionRef),
      makePanelMediaMaxGalleryItemsSelector(panelRef)
    ),
    maxYAxisCount: makeCascadingRawValueSelector(
      makeWorkspaceMediaMaxYAxisCountSelector(workspaceSettingsRef),
      makeSectionMediaMaxYAxisCountSelector(sectionRef),
      makePanelMediaMaxYAxisCountSelector(panelRef)
    ),
    actualSize: makeCascadingRawValueSelector(
      makeWorkspaceMediaActualSizeSelector(workspaceSettingsRef),
      makeSectionMediaActualSizeSelector(sectionRef),
      makePanelMediaActualSizeSelector(panelRef)
    ),
    fitToDimension: makeCascadingRawValueSelector(
      makeWorkspaceMediaFitToDimensionSelector(workspaceSettingsRef),
      makeSectionMediaFitToDimensionSelector(sectionRef),
      makePanelMediaFitToDimensionSelector(panelRef)
    ),
    alwaysOriginal: makeCascadingRawValueSelector(
      makeWorkspaceMediaAlwaysOriginalSelector(workspaceSettingsRef),
      makeSectionMediaAlwaysOriginalSelector(sectionRef),
      makePanelMediaAlwaysOriginalSelector(panelRef)
    ),
    gallerySettings: makeCascadingRawValueSelector(
      makeWorkspaceMediaGallerySettingsSelector(workspaceSettingsRef),
      makeSectionMediaGallerySettingsSelector(sectionRef),
      makePanelMediaGallerySettingsSelector(panelRef)
    ),
    gridSettings: makeCascadingRawValueSelector(
      makeWorkspaceMediaGridSettingsSelector(workspaceSettingsRef),
      makeSectionMediaGridSettingsSelector(sectionRef),
      makePanelMediaGridSettingsSelector(panelRef)
    ),
    compareSettings: makeCascadingRawValueSelector(
      makeWorkspaceMediaCompareSettingsSelector(workspaceSettingsRef),
      makeSectionMediaCompareSettingsSelector(sectionRef),
      makePanelMediaCompareSettingsSelector(panelRef)
    ),
    boundingBoxConfig: makeCascadingRawValueSelector(
      makeWorkspaceMediaBoundingBoxConfigSelector(workspaceSettingsRef),
      makeSectionMediaBoundingBoxConfigSelector(sectionRef),
      makePanelMediaBoundingBoxConfigSelector(panelRef)
    ),
    segmentationMaskConfig: makeCascadingRawValueSelector(
      makeWorkspaceMediaSegmentationMaskConfigSelector(workspaceSettingsRef),
      makeSectionMediaSegmentationMaskConfigSelector(sectionRef),
      makePanelMediaSegmentationMaskConfigSelector(panelRef)
    ),
    useRightHandedSystem: makeCascadingRawValueSelector(
      makeWorkspaceMediaUseRightHandedSystemSelector(workspaceSettingsRef),
      makeSectionMediaUseRightHandedSystemSelector(sectionRef),
      makePanelMediaUseRightHandedSystemSelector(panelRef)
    ),
    pointColor: makeCascadingRawValueSelector(
      makeWorkspaceMediaPointColorSelector(workspaceSettingsRef),
      makeSectionMediaPointColorSelector(sectionRef),
      makePanelMediaPointColorSelector(panelRef)
    ),
    object3DBackgroundColor: makeCascadingRawValueSelector(
      makeWorkspaceMediaObject3DBackgroundColorSelector(workspaceSettingsRef),
      makeSectionMediaObject3DBackgroundColorSelector(sectionRef),
      makePanelMediaObject3DBackgroundColorSelector(panelRef)
    ),
    disableVideoStream: makeCascadingRawValueSelector(
      makeWorkspaceMediaDisableVideoStreamSelector(workspaceSettingsRef),
      makeSectionMediaDisableVideoStreamSelector(sectionRef),
      makePanelMediaDisableVideoStreamSelector(panelRef)
    ),
    videoPlayerTimeStep: makeCascadingRawValueSelector(
      makeWorkspaceMediaVideoPlayerTimeStepSelector(workspaceSettingsRef),
      makeSectionMediaVideoPlayerTimeStepSelector(sectionRef),
      makePanelMediaVideoPlayerTimeStepSelector(panelRef)
    ),
    videoPlaybackSyncAutoplay: makeCascadingRawValueSelector(
      makeWorkspaceSettingsVideoPlaybackSyncAutoplaySelector(
        workspaceSettingsRef
      ),
      makeSectionMediaVideoPlaybackSyncAutoplaySelector(sectionRef),
      makePanelMediaVideoPlaybackSyncAutoplaySelector(panelRef)
    ),
    videoPlaybackSyncLoop: makeCascadingRawValueSelector(
      makeWorkspaceSettingsVideoPlaybackSyncLoopSelector(workspaceSettingsRef),
      makeSectionMediaVideoPlaybackSyncLoopSelector(sectionRef),
      makePanelMediaVideoPlaybackSyncLoopSelector(panelRef)
    ),
    // reversed override, videoPlaybackSync is inherited from the higher level
    videoPlaybackSync: createSelector(
      makeWorkspaceMediaVideoPlaybackSyncSelector(workspaceSettingsRef),
      makeSectionMediaVideoPlaybackSyncSelector(sectionRef),
      makePanelMediaVideoPlaybackSyncSelector(panelRef),
      (workspaceValue, sectionValue, panelValue) =>
        sectionValue ?? workspaceValue ?? panelValue
    ),
    sliderSync: makeCascadingRawValueSelector(
      makeWorkspaceMediaSliderSyncSelector(workspaceSettingsRef),
      makeSectionMediaSliderSyncSelector(sectionRef),
      () => undefined // sliderSync is not settable at the panel level
    ),
  });

  return createSelector(
    makePanelMediaBrowserConfigSelector(panelRef),
    cascadedFieldsSelector,
    (panelConfig, cascadedFields): MediaBrowserPanelConfig => ({
      // panel-only keys (chartTitle, mediaKeys, stepIndex, selection, etc.)
      ...panelConfig,
      ...cascadedFields,
    })
  );
};

/**
 * Lower level settings should always take precedence over higher level settings.
 */
export function getCascadedMediaSettings(
  higherLevelSettings: MediaBrowserPanelConfig | undefined,
  lowerLevelSettings: MediaBrowserPanelConfig | undefined
): MediaBrowserPanelConfig {
  return {
    ...higherLevelSettings,
    ...lowerLevelSettings,
    // reversed override, videoPlaybackSync should be inherited from the higher level
    videoPlaybackSync:
      higherLevelSettings?.videoPlaybackSync ??
      lowerLevelSettings?.videoPlaybackSync,
  };
}

/**
 * Workspace → section cascaded media settings (the merged object previously
 * provided via `inheritedSettings.media` on the settings contexts).
 */
export const makeInheritedMediaSettingsSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined
): Selector<RootState, MediaBrowserPanelConfig> =>
  createSelector(
    workspaceSettingsRef != null
      ? makeWorkspaceSettingsMediaSelector(workspaceSettingsRef)
      : () => undefined,
    makeSectionMediaSettingsSelector(sectionRef),
    (workspaceMediaSettings, sectionMediaSettings) =>
      getCascadedMediaSettings(workspaceMediaSettings, sectionMediaSettings)
  );
